/**
 * Chat Store — Zustand
 *
 * Manages channels, messages, and real-time updates.
 */

import { create } from "zustand";
import { api } from "../lib/api";
import { ws } from "../lib/ws";
import { showNotification, updateTitleBadge } from "../lib/notifications";
import { e2eeLog } from "../lib/e2ee-logger";
import { reportE2eeError } from "../lib/e2ee-telemetry";
import { humanizeFileTokens, stripMarkdownForPreview, isRealPlaintext } from "../lib/safeMessageText";
import { withBusy } from "../lib/busy-guard";

/**
 * Serialize processing of incoming WS "message" frames. Without this, an
 * X3DH/sender-key envelope (channelId === "__system__") and a following
 * group_message can land within the same emit-tick — savePeerSenderKey is an
 * async IDB write, but the next frame's decryptGroupMessage already runs
 * loadPeerSenderKey before that write commits. Result: first group message
 * after a fresh sender-key distribution decrypts as null, only heals when a
 * later reload re-pulls messages.
 *
 * Module-scope chain survives reconnects; .catch keeps one failed decrypt
 * from poisoning the queue for everything that follows.
 */
let incomingMessageChain: Promise<void> = Promise.resolve();

/** Returns true for any E2EE content type the client must decrypt (pairwise or group). */
const isE2EEContentType = (ct: string | undefined | null): ct is "e2ee" | "e2ee_group" =>
  ct === "e2ee" || ct === "e2ee_group";

/**
 * Build the sidebar-preview-friendly representation of a message:
 * lock-icon fallback when no plaintext, markdown-stripped + file-token-humanized preview.
 * Used wherever channels[].lastMessage is written so addMessage / updateMessage /
 * loadMessages stay in lock-step. Without this, late-decrypt paths (sender-key arrival,
 * backup fallback, dm_recover_replaced) update messages map but leave sidebar stuck on
 * the original placeholder.
 */
function buildSidebarLastMessage<T extends { plaintext?: string; ciphertext?: string | null; contentType?: string | null }>(message: T): T {
  const previewText = message.plaintext
    ?? (message.ciphertext?.startsWith("{") && message.ciphertext?.includes('"v"')
      ? "🔒 Verschlüsselte Nachricht"
      : isE2EEContentType(message.contentType)
        ? "🔒 Verschlüsselte Nachricht"
        : message.ciphertext ?? "");
  return { ...message, plaintext: stripMarkdownForPreview(humanizeFileTokens(previewText)) };
}

/**
 * Request missing sender keys for all group channels.
 * Called after crypto init and after channels load to cover both timing orders.
 */
let proactiveKeyFetchDone = false;
async function requestMissingSenderKeys(get: () => { channels: Array<{ id: string; type: string; members?: Array<{ userId: string }> }> }): Promise<void> {
  if (proactiveKeyFetchDone) return;
  const cryptoState = useCrypto.getState();
  if (!cryptoState.initialized) return;
  const groupChannels = get().channels.filter((ch) => ch.type === "group" && ch.members);
  if (groupChannels.length === 0) return; // channels not loaded yet

  proactiveKeyFetchDone = true;
  try {
    const { loadPeerSenderKeysByUser } = await import("../lib/crypto-storage");
    const { requestSenderKey } = await import("./useCrypto-group");
    const { useAuth } = await import("./useAuth");
    const currentUserId = useAuth.getState().user?.id;
    for (const ch of groupChannels) {
      for (const m of ch.members!) {
        if (m.userId === currentUserId) continue;
        // Per-device storage — we want the prefetch to fire only if we have
        // ZERO keys from this user. Once recovery/distribute delivers any
        // device slot, the catch-block-driven per-device fallback handles
        // the rest as messages arrive from other devices.
        const existing = await loadPeerSenderKeysByUser(ch.id, m.userId);
        if (existing.length === 0) {
          requestSenderKey(ch.id, m.userId);
        }
      }
    }
  } catch (err) {
    proactiveKeyFetchDone = false; // allow retry
    e2eeLog.warn("[chat] Failed to request missing sender keys:", err);
  }
}

// Notification sound via Web Audio API
let audioCtx: AudioContext | null = null;
/** Throttle: collapse bursts (catch-up on reconnect, multiple back-to-back messages)
 *  into a single ping. Without this, opening the app after downtime = one pling
 *  per missed message. */
let lastPlingAt = 0;
const PLING_THROTTLE_MS = 1500;
function playNotificationSound() {
  const now = Date.now();
  if (now - lastPlingAt < PLING_THROTTLE_MS) return;
  lastPlingAt = now;
  try {
    if (!audioCtx) audioCtx = new AudioContext();
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    osc.frequency.setValueAtTime(880, audioCtx.currentTime); // A5
    osc.frequency.setValueAtTime(1046, audioCtx.currentTime + 0.08); // C6
    gain.gain.setValueAtTime(0.15, audioCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.25);
    osc.start(audioCtx.currentTime);
    osc.stop(audioCtx.currentTime + 0.25);
  } catch {
    // Audio not available — silent
  }
}
import { useCrypto, selfDecrypt, isValidBotChannelSenderKeyEnvelope, _registerDeviceWithPhantomRetry } from "./useCrypto";
import { handleDeviceDeactivated } from "./device-heal";

/**
 * Wait for E2EE crypto to be initialized.
 * Returns immediately if already initialized, otherwise waits up to timeoutMs.
 * This is the central "ready gate" — all decrypt-dependent flows should await this.
 */
function waitForCryptoInit(timeoutMs = 10000): Promise<boolean> {
  if (useCrypto.getState().initialized) return Promise.resolve(true);
  return new Promise((resolve) => {
    const timer = setTimeout(() => { unsub(); resolve(false); }, timeoutMs);
    const unsub = useCrypto.subscribe((state) => {
      if (state.initialized) {
        clearTimeout(timer);
        unsub();
        resolve(true);
      }
    });
  });
}

// ============================================================
// Shared Decrypt Helper
// ============================================================

import { cachePlaintext, getCachedPlaintext, getCachedPlaintexts, deleteCachedPlaintext, saveMessageList, loadMessageList } from "../lib/crypto-storage";
import { shouldSkipOwnReread, makeOwnMissMarker, OWN_MISS_PREFIX } from "./own-read-cache";
import { mergeMessagesPreservingPlaintext, countRealUnread, bumpUnread, isPlaceholderTail, applyChannelDeleted } from "../lib/chat-store-helpers";

/**
 * Local plaintext cache for own sent messages.
 * Since we encrypt for the recipient, we can't decrypt our own messages later.
 * Two layers:
 * 1. pendingPlaintexts: ciphertext prefix -> plaintext (before we know the message ID)
 * 2. IndexedDB plaintextCache: messageId -> plaintext (persistent across reloads)
 * 3. ownMessageCache: messageId -> plaintext (in-memory fast lookup)
 */
const ownMessageCache = new Map<string, string>();
const pendingPlaintexts = new Map<string, string>();

// (C) Loop guard for auto-resend on e2ee_session_reset.
// If the rewrapped message ALSO fails to decrypt (peer still broken), the
// peer fires another reset for the same failedMessageId. Without this set
// we'd auto-rewrap-forever. Each id is held for 60s — long enough that any
// in-flight follow-up reset is suppressed, short enough that a genuinely
// new failure later (different fault) goes through.
const recentlyAutoRetriedResets = new Set<string>();

interface PendingGroupMessage {
  message: Message;
  channelId: string;
  senderUserId: string;
  /** Fehlt in manchen Umschlägen — solche Nachrichten parken und heilen über
   *  den C5-Empfängerpfad (siehe Kommentar an der Ablagestelle). */
  senderDeviceId?: string;
  addedAt: number;
  giveUpTimer: ReturnType<typeof setTimeout>;
}
const pendingGroupMessages = new Map<string, PendingGroupMessage>();
const PENDING_GROUP_TIMEOUT_MS = 30_000;

/** Store plaintext before sending (keyed by ciphertext prefix) */
export function cachePendingPlaintext(ciphertext: string, plaintext: string): void {
  const key = ciphertext.slice(0, 64);
  pendingPlaintexts.set(key, plaintext);
  setTimeout(() => pendingPlaintexts.delete(key), 30000);
}

/** Try to resolve plaintext from pending cache and promote to persistent cache */
function resolvePendingPlaintext(messageId: string, ciphertext: string, channelId?: string): string | undefined {
  const key = ciphertext.slice(0, 64);
  const plaintext = pendingPlaintexts.get(key);
  if (plaintext) {
    pendingPlaintexts.delete(key);
    cacheAndBackup(messageId, plaintext, channelId);
    if (ownMessageCache.size > 1000) {
      const firstKey = ownMessageCache.keys().next().value;
      if (firstKey) ownMessageCache.delete(firstKey);
    }
    return plaintext;
  }
  return undefined;
}

/** Store plaintext of a sent message so we can display it later */
export function cacheOwnMessage(messageId: string, plaintext: string, channelId?: string, force = false): void {
  cacheAndBackup(messageId, plaintext, channelId, force);
  if (ownMessageCache.size > 1000) {
    const firstKey = ownMessageCache.keys().next().value;
    if (firstKey) ownMessageCache.delete(firstKey);
  }
}

/** DM-Recovery (B): triggered when a peer DM cannot be decrypted. Fires the
 *  WS request to the specific sender device and arms a 10s timer; if no
 *  dm_recover_replaced arrives in that window, the message is marked
 *  unrecoverable in the local store so the user sees a clear failure state
 *  instead of the generic "konnte nicht entschlüsselt werden" forever. */
const DM_RECOVERY_TIMEOUT_MS = 10_000;
const DM_RECOVERY_UNRECOVERABLE_TEXT = "[Diese Nachricht konnte nicht wiederhergestellt werden — bitte Sender erneut senden lassen]";
const dmRecoveryTimers = new Map<string, ReturnType<typeof setTimeout>>();

function tryDmRecovery(
  msgId: string,
  channelId: string,
  senderUserId: string,
  envelopeSenderDeviceId: string | undefined,
  ourDeviceId: string | null | undefined,
): void {
  if (!envelopeSenderDeviceId || !ourDeviceId) return;
  // Guard: live WS messages can reach the decrypt-fail path with channelId
  // still undefined. Calling .startsWith on it threw
  // `TypeError: undefined is not an object (evaluating 't.startsWith')`, which
  // crashed the ENTIRE decrypt cascade — skipping the F22 plaintext-failsafe and
  // the clean "[…konnte nicht entschlüsselt werden]" fallback, then re-throwing
  // in the catch. Without a channelId we can't issue a recovery request anyway,
  // so bail quietly and let the rest of the cascade run.
  if (!channelId) return;
  // Don't recover from our own messages — they go through the own-message path.
  // Don't recover system channels (sender_key relay, etc.) — different protocol.
  if (channelId.startsWith("__")) return;

  void import("./useCrypto-encrypt").then(({ requestDmRecovery, getDmRecoveryAttemptCount }) => {
    if (getDmRecoveryAttemptCount(msgId) >= 3) {
      // Already exhausted. Skip both the WS send and the timer — keep the
      // existing error string visible so the user knows something's stuck.
      return;
    }
    const sent = requestDmRecovery(msgId, channelId, senderUserId, envelopeSenderDeviceId, ourDeviceId);
    if (!sent) return;

    const existing = dmRecoveryTimers.get(msgId);
    if (existing) clearTimeout(existing);
    const t = setTimeout(() => {
      dmRecoveryTimers.delete(msgId);
      // Only override the displayed text if we still don't have a real plaintext.
      // The dm_recover_replaced handler will have called updateMessage with the
      // decrypted text by now if recovery succeeded.
      const current = useChat.getState().messages.get(channelId)?.find((m) => m.id === msgId);
      if (current && current.plaintext && current.plaintext.startsWith("[")) {
        useChat.getState().updateMessage(msgId, channelId, { plaintext: DM_RECOVERY_UNRECOVERABLE_TEXT });
      }
    }, DM_RECOVERY_TIMEOUT_MS);
    dmRecoveryTimers.set(msgId, t);
  }).catch(() => { /* import error — best-effort, no recovery */ });
}

/**
 * Backup-key fallback: try to decrypt a message from server-side backup.
 * Used when no per-device ciphertext exists (e.g. new device logged in after message was sent).
 */
const backupFetchInFlight = new Set<string>();
/** Messages where the server confirmed no backup exists (404) — skip future attempts */
const backupNotFound = new Set<string>();
/** Messages where a transient error (429, 5xx, network) occurred — skip for a short cooldown.
 *  Value = timestamp when the cooldown expires. Prevents a rate-limit-induced retry storm:
 *  without this, 429 was NOT cached → every clearMessagesCache refetch × 50 messages fed the
 *  rate-limiter, logging the user out. */
const backupTransientSkipUntil = new Map<string, number>();
const BACKUP_TRANSIENT_COOLDOWN_MS = 60_000;
/** Sentinel: tryBackupDecrypt returns this when backup key isn't ready yet (temporary, NOT a 404) */
const BACKUP_KEY_NOT_READY = Symbol("BACKUP_KEY_NOT_READY");
/**
 * Well-known key for bot message backups.
 * Must match BOT_BACKUP_KEY_MATERIAL in bot.service.ts on the server.
 * Not a secret — bot messages aren't truly E2EE (server sees plaintext).
 */
const BOT_BACKUP_KEY_MATERIAL = "kollegeplus:bot-backup:v1";
let _botBackupCryptoKey: CryptoKey | null = null;

async function getBotBackupKey(): Promise<CryptoKey> {
  if (!_botBackupCryptoKey) {
    const encoder = new TextEncoder();
    const hashBuffer = await crypto.subtle.digest("SHA-256", encoder.encode(BOT_BACKUP_KEY_MATERIAL));
    _botBackupCryptoKey = await crypto.subtle.importKey(
      "raw", hashBuffer, { name: "AES-GCM" }, false, ["decrypt"],
    );
  }
  return _botBackupCryptoKey;
}

async function tryBackupDecrypt(msg: { id: string; channelId: string }): Promise<string | null | typeof BACKUP_KEY_NOT_READY> {
  // Guard: channelId must be a valid UUID (skip __system__ and other special channels)
  if (!msg.channelId || msg.channelId.startsWith("__")) return null;
  // Skip if we already know there's no backup for this message
  if (backupNotFound.has(msg.id)) return null;
  // Transient-error cooldown — avoids re-feeding the rate-limiter
  const until = backupTransientSkipUntil.get(msg.id);
  if (until !== undefined) {
    if (until > Date.now()) return null;
    backupTransientSkipUntil.delete(msg.id);
  }
  // Prevent concurrent fetches for the same message
  if (backupFetchInFlight.has(msg.id)) return null;

  // F15e: helper — channel-shared recovery key fallback. Only fires for
  // group channels; DM channels are out of scope (single peer, the
  // per-user backup already covers them). Best-effort, never throws.
  // F19: telemetry is recorded inside tryChannelRecoveryDecrypt itself.
  const tryChannelRecovery = async (): Promise<string | null> => {
    try {
      const ch = useChat.getState().channels.find((c) => c.id === msg.channelId);
      if (ch?.type !== "group") return null;
      const { tryChannelRecoveryDecrypt } = await import("../lib/channel-recovery-backup");
      return await tryChannelRecoveryDecrypt(msg.channelId, msg.id);
    } catch {
      return null;
    }
  };

  try {
    const { restoreBackupKeyFromCache, decryptBackup } = await import("../lib/backup-key");
    const backupKey = await restoreBackupKeyFromCache();

    backupFetchInFlight.add(msg.id);
    let backup: { ciphertext: string; nonce: string } | null = null;
    try {
      backup = await api.get<{ ciphertext: string; nonce: string }>(
        `/channels/${msg.channelId}/messages/${msg.id}/backup`,
      );
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      if (status === 404) {
        // F15e: per-user backup missing — try the channel-shared recovery backup
        // BEFORE we mark backupNotFound. Group decrypt failures from a single
        // member used to lose backlog for everyone; the channel recovery key
        // (distributed via X3DH) lets ANY member fall through to this path.
        const recovered = await tryChannelRecovery();
        if (recovered !== null) return recovered;
        backupNotFound.add(msg.id);
        return null;
      }
      if (status === 429 || (typeof status === "number" && status >= 500)) {
        backupTransientSkipUntil.set(msg.id, Date.now() + BACKUP_TRANSIENT_COOLDOWN_MS);
      }
      return null;
    }
    if (!backup?.ciphertext || !backup?.nonce) {
      // No per-user backup — try the channel-shared fallback before marking
      const recovered = await tryChannelRecovery();
      if (recovered !== null) return recovered;
      backupNotFound.add(msg.id);
      return null;
    }

    // F19: per-user backup row successfully fetched.
    const { recordBackupRead, recordBackupDecryptOutcome } = await import("../lib/backup-telemetry");
    recordBackupRead("per_user", 1);

    // Try user backup key first
    if (backupKey) {
      try {
        const pt = await decryptBackup(backupKey, backup.ciphertext, backup.nonce);
        recordBackupDecryptOutcome("per_user", true);
        return pt;
      } catch (err) {
        // User backup key failed — try bot backup key below.
        // Reason carries WebCrypto error name (OperationError = AES-GCM tag mismatch, etc.)
        const errName = (err as { name?: string } | null)?.name ?? "unknown";
        recordBackupDecryptOutcome("per_user", false, `user-key:${errName}`);
      }
    } else {
      // No backupKey loaded for this user/device at fetch time — distinct from a tag mismatch.
      recordBackupDecryptOutcome("per_user", false, "no-backup-key");
      // Bug D: trigger pull from peer devices so future decrypts succeed.
      import("./useCrypto").then(({ requestUserBackupKey }) => { requestUserBackupKey(); }).catch(() => {});
    }

    // Try bot backup key (for messages sent by Lara bot)
    try {
      const botKey = await getBotBackupKey();
      const pt = await decryptBackup(botKey, backup.ciphertext, backup.nonce);
      recordBackupDecryptOutcome("bot", true);
      return pt;
    } catch (err) {
      // Neither key worked
      const errName = (err as { name?: string } | null)?.name ?? "unknown";
      recordBackupDecryptOutcome("bot", false, `bot-key:${errName}`);
    }

    // F15e: per-user ciphertext existed but neither key decrypted it. Try
    // the channel-shared recovery backup as a last resort before giving up.
    const recovered = await tryChannelRecovery();
    if (recovered !== null) return recovered;

    if (!backupKey) {
      return BACKUP_KEY_NOT_READY;
    }
    return null;
  } catch (err: unknown) {
    // Outer catch — non-API errors (decrypt throws, import failures, etc.).
    // The per-user 404 / 429 paths are already handled inside the try above.
    const status = (err as { status?: number })?.status;
    if (status === 404) {
      backupNotFound.add(msg.id);
    } else if (status === 429 || (typeof status === "number" && status >= 500)) {
      backupTransientSkipUntil.set(msg.id, Date.now() + BACKUP_TRANSIENT_COOLDOWN_MS);
    }
    return null;
  } finally {
    backupFetchInFlight.delete(msg.id);
  }
}

/**
 * F19 owner-scoped plaintext fallback. Returns the per-user
 * `debug_backup_plaintext` row for a message the CURRENT user sent, when
 * the encrypted-backup chain has already failed. Caller MUST gate on
 * `msg.senderId === currentUserId` — the server enforces the same check
 * but the client guard prevents a wasteful round-trip for peer messages.
 *
 * Used for the multi-device DM case where the sender's outgoing fan-out
 * silently dropped the envelope for another of their own devices (see
 * `useCrypto-encrypt.ts` fan-out retry). Server gates on the F19 flag +
 * allow-list and audit-logs every read. Best-effort, never throws.
 */
/**
 * F22 peer-scoped plaintext failsafe. Returns server-side plaintext for any
 * channel member when the full E2EE decrypt cascade (incl. F15e channel
 * recovery backup) has already failed. Backed by the F19 plaintext mirror
 * (`debug_backup_plaintext`) but exposed via a member-readable, audited
 * endpoint — separate from the admin debug route. Server enforces channel
 * membership, message-belongs-to-channel, and the global F19 kill-switch.
 *
 * Best-effort, never throws. Use this as the LAST resort in the decrypt
 * cascade, after every encrypted-recovery path has returned null.
 */
// P1a: cascadeFailReason — narrow why we ended up at the F22 last-resort,
// so the server-side audit row can later be aggregated per failure-class
// (Sunset-Plan Phase 1). Kept narrow on purpose — server enforces the
// allowlist; anything else is dropped to "unknown".
export type CascadeFailReason =
  | "sender-key-miss"
  | "ratchet-fail"
  | "x3dh-stale"
  | "recovery-404"
  | "recovery-decrypt-fail"
  | "unknown";

// P1b: 404-backoff delays (ms). Mirror row is written best-effort + async;
// client may arrive before the row exists. Retry up to 3 times with
// 1s → 3s → 10s backoff. Only fires on HTTP 404 — non-404 errors fall
// through immediately so we don't add latency to real failures.
// Bounded: max 3 attempts × 1 msg, never storms (in-flight guard prevents
// concurrent calls for the same key).
const _FAILSAFE_404_BACKOFF_MS = [1_000, 3_000, 10_000] as const;

// P2: Persistent dedup for the peer-plaintext-failsafe.
//
// Problem: peerPlaintextFailsafeInFlight only deduplicates CONCURRENT requests.
// A reload / new tab / React re-render clears it, so the same ~966 round-trips
// repeat for ~143 distinct events (6.7× refetch/event, 81% fail-rate).
//
// Solution: mirror the __NO_BACKUP__ idiom already used by tryBackupDecrypt:
//   - in-memory Set  (peerPlaintextFailsafeMissed) for the fast in-process path
//   - IDB marker     "__NO_FAILSAFE__:<expiry-ms>" written into plaintextCache
//     (same store — no new storage layer)
//
// IDB key shape: messageId (same key as plaintext row)
// IDB value:     "__NO_FAILSAFE__:<unix-ms-expiry>"   (10 min TTL)
//
// CRITICAL invalidation: when sender-key-received fires (or sender_key_identity_reset
// completes) for a channel, call clearPeerPlaintextFailsafeMarkers(channelId) so
// messages that were previously stuck can re-decrypt with the freshly delivered key.
// Without this, a key-miss that got cached as "no-failsafe" would stay stuck even
// after the real key arrives.
const FAILSAFE_MISS_TTL_MS = 10 * 60 * 1000; // 10 minutes
const FAILSAFE_MISS_PREFIX = "__NO_FAILSAFE__:";

/** In-memory fast-path: keyed as "channelId|messageId", value = expiry timestamp */
const peerPlaintextFailsafeMissed = new Map<string, number>();

/**
 * In-memory 60-second negative cache for own-message server reads.
 * Keyed by messageId, value = expiry timestamp (ms).
 * Prevents the own-fallback server call from firing on every render when
 * tryOwnPlaintextFallback returned null for a group message with __NO_BACKUP__.
 * Short TTL (60s) ensures a delayed backup write can still surface quickly.
 */
const ownMissCache = new Map<string, number>();

/**
 * P2: Clear all in-memory and IDB failsafe-miss markers for a given channel.
 * Must be called whenever a new sender key arrives (sender-key-received) or
 * an identity reset completes (sender_key_identity_reset), so that messages
 * previously stuck can retry decryption with the fresh key.
 * Best-effort: IDB deletions run fire-and-forget.
 */
function clearPeerPlaintextFailsafeMarkers(channelId: string): void {
  // Clear in-memory markers for this channel
  for (const k of peerPlaintextFailsafeMissed.keys()) {
    if (k.startsWith(`${channelId}|`)) {
      peerPlaintextFailsafeMissed.delete(k);
    }
  }
  // Clear IDB markers: we don't track which messageIds belong to a channel
  // in the plaintext-miss map, so we rely on the in-memory clear above for
  // fast re-entry and let the IDB TTL expire naturally. IDB markers are only
  // consulted AFTER the in-memory check, so once the in-memory entry is gone
  // the IDB marker is effectively dead for this session. On next load the IDB
  // entry would be consulted — but sender-key-received fires on reconnect before
  // decrypts run, so clearPeerPlaintextFailsafeMarkers is called before the next
  // loadMessages sweep. For belt-and-suspenders we also record the channel in an
  // invalidated set so IDB reads for that channel ignore stale markers.
  peerPlaintextFailsafeChannelInvalidated.add(channelId);
  e2eeLog.log("[chat] P2: cleared peer-plaintext-failsafe markers for channel", channelId);
}

/**
 * P2: Channels whose failsafe markers have been invalidated this session by
 * a new sender key or identity reset. IDB markers for these channels are
 * ignored until they are removed from the map (they expire after TTL anyway).
 * Belt-and-suspenders on top of the in-memory peerPlaintextFailsafeMissed clear.
 */
const peerPlaintextFailsafeChannelInvalidated = new Set<string>();

/** P2: record a definitive failsafe miss (in-memory + IDB, TTL) so re-renders /
 *  reloads skip the round-trip until the TTL expires or a key arrives. */
function markFailsafeMiss(key: string, messageId: string): void {
  const expiry = Date.now() + FAILSAFE_MISS_TTL_MS;
  peerPlaintextFailsafeMissed.set(key, expiry);
  cachePlaintext(messageId, `${FAILSAFE_MISS_PREFIX}${expiry}`).catch(() => {});
}

const peerPlaintextFailsafeInFlight = new Set<string>();
async function tryPeerPlaintextFailsafe(
  channelId: string,
  messageId: string,
  cascadeFailReason: CascadeFailReason = "unknown",
  senderDeviceId?: string,
): Promise<string | null> {
  if (!channelId || channelId.startsWith("__")) return null;
  const key = `${channelId}|${messageId}`;

  // P2: in-memory miss guard — survives re-renders, cleared on sender-key-received
  const missExpiry = peerPlaintextFailsafeMissed.get(key);
  if (missExpiry !== undefined) {
    if (Date.now() < missExpiry) return null;
    peerPlaintextFailsafeMissed.delete(key); // TTL expired, allow retry
  }

  // P2: IDB miss guard — survives reloads/new-tabs; skip if channel was invalidated this session
  if (!peerPlaintextFailsafeChannelInvalidated.has(channelId)) {
    try {
      const idbMarker = await getCachedPlaintext(messageId);
      if (idbMarker?.startsWith(FAILSAFE_MISS_PREFIX)) {
        const expiry = parseInt(idbMarker.slice(FAILSAFE_MISS_PREFIX.length), 10);
        if (!isNaN(expiry) && Date.now() < expiry) {
          // Promote to in-memory to avoid IDB round-trip on next render cycle
          peerPlaintextFailsafeMissed.set(key, expiry);
          return null;
        }
        // Marker expired — remove from IDB so it doesn't accumulate stale rows
        deleteCachedPlaintext(messageId).catch(() => {});
      }
    } catch {
      // IDB read failure — best-effort, proceed with the fetch
    }
  }

  if (peerPlaintextFailsafeInFlight.has(key)) return null;
  peerPlaintextFailsafeInFlight.add(key);
  try {
    const { recordBackupRead, recordBackupDecryptOutcome } = await import(
      "../lib/backup-telemetry"
    );
    recordBackupRead("peer_plaintext_failsafe");
    const qs = new URLSearchParams({ reason: cascadeFailReason });
    if (senderDeviceId) qs.set("senderDeviceId", senderDeviceId);
    const url = `/channels/${channelId}/messages/${messageId}/plaintext-failsafe?${qs.toString()}`;

    // P1b: retry loop — only on 404 (mirror write race), max 3 attempts.
    for (let attempt = 0; attempt <= _FAILSAFE_404_BACKOFF_MS.length; attempt++) {
      try {
        const resp = await api.get<{
          plaintext: string;
          mechanism: "channel_shared" | "per_user";
        }>(url);
        if (resp?.plaintext) {
          recordBackupDecryptOutcome("peer_plaintext_failsafe", true);
          return resp.plaintext;
        }
        // P2: empty response = definitive miss (not transient) → persist marker
        // so re-renders / reloads skip the round-trip until TTL or key-arrival.
        recordBackupDecryptOutcome("peer_plaintext_failsafe", false, "empty-row");
        markFailsafeMiss(key, messageId);
        return null;
      } catch (err: unknown) {
        const status = (err as { status?: number })?.status;
        if (status === 404 && attempt < _FAILSAFE_404_BACKOFF_MS.length) {
          // P1b: mirror row not yet written — wait and retry.
          await new Promise<void>((resolve) =>
            setTimeout(resolve, _FAILSAFE_404_BACKOFF_MS[attempt]),
          );
          continue;
        }
        // Non-404 error or exhausted retries — record and bail.
        const tag = typeof status === "number" ? `http-${status}` : "no-status";
        recordBackupDecryptOutcome("peer_plaintext_failsafe", false, tag);
        // P2: a 404 that survived all retries OR a 410 (row existed but TTL-
        // expired) is a definitive miss → cache it. A 410 will never recover, so
        // there is no point retrying. Transient errors (429, 5xx, network) are
        // NOT cached — let them retry.
        if (status === 404 || status === 410) markFailsafeMiss(key, messageId);
        return null;
      }
    }
    // Unreachable — loop always returns or continues, but TypeScript needs this.
    return null;
  } catch {
    return null;
  } finally {
    peerPlaintextFailsafeInFlight.delete(key);
  }
}

const ownPlaintextFetchInFlight = new Set<string>();
async function tryOwnPlaintextFallback(messageId: string): Promise<string | null> {
  if (ownPlaintextFetchInFlight.has(messageId)) return null;
  ownPlaintextFetchInFlight.add(messageId);
  try {
    const { recordBackupDecryptOutcome } = await import("../lib/backup-telemetry");
    try {
      const resp = await api.get<{ messageId: string; plaintext: string }>(
        `/users/me/debug-backup-plaintext/${messageId}`,
      );
      if (resp.plaintext) {
        recordBackupDecryptOutcome("per_user_plaintext_fallback", true);
        return resp.plaintext;
      }
      recordBackupDecryptOutcome("per_user_plaintext_fallback", false, "empty-row");
      return null;
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      const tag = typeof status === "number" ? `http-${status}` : "no-status";
      recordBackupDecryptOutcome("per_user_plaintext_fallback", false, tag);
      return null;
    }
  } catch {
    return null;
  } finally {
    ownPlaintextFetchInFlight.delete(messageId);
  }
}

/** Decrypt a single message. Returns null for sender_key messages (skip display). */
/** Track which messages have already been queued for backup to avoid duplicates */
const backedUpMessages = new Set<string>();

/** Cache plaintext + upload backup (fire-and-forget, deduplicated).
 *
 *  F15e: when `channelId` is provided and the channel is a group, ALSO
 *  upload the channel-shared recovery backup (channelRecoveryKey concept —
 *  group-only, DMs have no such key).
 *  F24: the channel_shared *plaintext mirror* (backfillOneLive) is NOT
 *  group-only — it now also runs for DMs. The old assumption ("DMs are out
 *  of scope, the per-user backup is enough") was wrong: the per-user backup
 *  upload can itself fail (rate-limit, offline) and legacy DM sends carry no
 *  debugPlaintext, so a DM message could end up with no plaintext failsafe
 *  at all. The server route only checks channel membership, not type.
 *  We resolve the channel type from the local store (best-effort: if
 *  the channel isn't loaded yet, we skip both uploads silently).
 */
function cacheAndBackup(messageId: string, plaintext: string, channelId?: string, force = false): void {
  cachePlaintext(messageId, plaintext).catch(() => {});
  ownMessageCache.set(messageId, plaintext);
  // On edit the plaintext changed but messageId is unchanged — clear the dedup
  // so the backups (per-user + channel_shared mirror) re-upload the new text;
  // else cross-device recovery would restore the pre-edit content.
  if (force) backedUpMessages.delete(messageId);
  // Ansatz C: upload encrypted backup (non-blocking, dedup)
  if (!backedUpMessages.has(messageId)) {
    backedUpMessages.add(messageId);
    const hadBackupKey = !!useCrypto.getState().backupKey;
    // F24: the upload is still fire-and-forget from callers' point of view,
    // but uploadMessageBackup now resolves success/failure instead of
    // swallowing it silently (e.g. HTTP 429). On failure, undo the dedup
    // marker so a later cacheAndBackup call (re-render, edit, retry,
    // clearMessagesCache) retries the upload — previously a failed upload
    // was indistinguishable from a successful one from the dedup Set's
    // point of view and was never retried, leaving no backup row.
    useCrypto.getState().uploadMessageBackup(messageId, plaintext)
      .then((success) => {
        if (!success) backedUpMessages.delete(messageId);
      })
      .catch(() => backedUpMessages.delete(messageId));
    // If backup key was unavailable, remove from dedup so the upload is retried
    // once the key is loaded (clearMessagesCache re-triggers cacheAndBackup).
    if (!hadBackupKey) backedUpMessages.delete(messageId);
    // F15e/F24: piggy-back the channel-shared recovery uploads. Fire-and-forget;
    // never throws and never affects send/cache flow.
    if (channelId && !channelId.startsWith("__")) {
      try {
        const ch = useChat.getState().channels.find((c) => c.id === channelId);
        // F15e: channel-shared recovery backup stays GROUP-ONLY — it depends
        // on a local channelRecoveryKey concept that doesn't exist for DMs.
        if (ch?.type === "group") {
          void import("../lib/channel-recovery-backup")
            .then(({ uploadChannelRecoveryBackup }) =>
              uploadChannelRecoveryBackup(channelId, messageId, plaintext),
            )
            .catch(() => {});
        }
        // F19/F24 write-gap fix: also seed the member-readable channel_shared
        // mirror directly, for BOTH group and DM channels. uploadChannelRecoveryBackup
        // no-ops without a local channelRecoveryKey, and the per-user backup's
        // sender→channel_shared seeding never fires for group sends — so a message
        // missed by every receiving device used to leave NO failsafe row
        // (chicken-and-egg). F24: DMs used to be excluded here on the assumption
        // that the per-user backup is "enough" for a single-peer chat, but that
        // upload can itself fail (rate-limit, offline) and legacy DM sends carry
        // no debugPlaintext — so a DM message could end up with no failsafe at
        // all. The server route only checks channel membership, not type.
        // This live backfill runs whenever we hold the plaintext (own send or
        // any successful decrypt), gated by the same F19 opt-in + server gates.
        if (ch?.type === "group" || ch?.type === "dm") {
          void import("../lib/plaintext-backfill")
            .then(({ backfillOneLive }) =>
              backfillOneLive(channelId, messageId, plaintext),
            )
            .catch(() => {});
        }
      } catch {
        // store access failure — ignore, primary backup path is intact
      }
    }
    // Prevent memory leak: cap set size
    if (backedUpMessages.size > 2000) {
      const first = backedUpMessages.values().next().value;
      if (first) backedUpMessages.delete(first);
    }
  }
}

async function decryptSingleMessage(msg: Message): Promise<Message | null> {
  const crypto = useCrypto.getState();

  // For non-E2EE messages, use ciphertext as plaintext directly
  if (!isE2EEContentType(msg.contentType)) {
    return { ...msg, plaintext: msg.ciphertext };
  }

  // P2: Re-render guard — if this message already has a real plaintext in the
  // Zustand store (i.e. msg came from state and was already decrypted) skip the
  // entire cascade. Placeholders (strings starting with "[") are intentionally
  // excluded so that stuck messages can still be re-retried on the next cycle.
  if (msg.plaintext && !msg.plaintext.startsWith("[")) {
    return msg;
  }

  // E2EE message but crypto not initialized — try backup decrypt as fallback
  if (!crypto.initialized) {
    // Don't give up — the backup key lives in localStorage and doesn't need full crypto init
    const backupFallback = await tryBackupDecrypt(msg);
    if (typeof backupFallback === "string") {
      cacheAndBackup(msg.id, backupFallback, msg.channelId);
      return { ...msg, plaintext: backupFallback };
    }
    return { ...msg, plaintext: "[Verschlüsselung wird initialisiert…]" };
  }

  // Check in-memory cache first (covers multi-device messages where ciphertext is null)
  const cached = ownMessageCache.get(msg.id);
  if (cached) {
    return { ...msg, plaintext: cached };
  }

  // Check in-memory backup restore cache (Safari/iOS IDB timing workaround)
  const { _restoredPlaintexts } = await import("./useCrypto");
  const restoredPt = _restoredPlaintexts.get(msg.id);
  if (restoredPt) {
    ownMessageCache.set(msg.id, restoredPt); // promote to fast cache
    return { ...msg, plaintext: restoredPt };
  }

  // Check IndexedDB persistent cache early (for ALL messages — own + received)
  // This is needed for multi-device E2EE where the messages table has no ciphertext
  // (ciphertext lives in message_recipients per-device), so after reload we need the cache.
  let idbCached = await getCachedPlaintext(msg.id);
  // FAILSAFE_MISS marker must never reach the UI as plaintext. Valid marker:
  // failsafe already 404'd within the TTL — show a placeholder without a
  // server round-trip. Expired marker: drop it and run the full decrypt
  // cascade below (the failsafe may have data now, e.g. a bot-mechanism row).
  if (idbCached?.startsWith(FAILSAFE_MISS_PREFIX)) {
    const missExpiry = parseInt(idbCached.slice(FAILSAFE_MISS_PREFIX.length), 10);
    if (Number.isFinite(missExpiry) && Date.now() < missExpiry) {
      return { ...msg, plaintext: "[Verschlüsselte Nachricht]" };
    }
    // undefined, nicht null: getCachedPlaintext liefert `string | undefined`,
    // und die Prüfungen unten fragen ohnehin nur auf Wahrheitswert ab.
    idbCached = undefined;
  }
  if (idbCached) {
    // OWN_MISS marker: own-fallback server read already returned null within the last 60s.
    // Promote to in-memory cache and short-circuit without a server call.
    // Expired markers fall through so the server is retried after the TTL.
    if (shouldSkipOwnReread(idbCached, Date.now())) {
      // Promote expiry to in-memory so subsequent renders don't hit IDB
      const expiry = parseInt(idbCached.slice(OWN_MISS_PREFIX.length), 10);
      ownMissCache.set(msg.id, expiry);
      return { ...msg, plaintext: "[Eigene Nachricht – Verschlüsselung aktiv]" };
    }

    // __NO_BACKUP__ marker means we already checked and no backup exists
    if (idbCached === "__NO_BACKUP__") {
      // Hoist isOwn before the isGroupMsg branch so we can use it in both paths.
      const { useAuth } = await import("./useAuth");
      const currentUserId = useAuth.getState().user?.id;
      const isOwn = !!(currentUserId && msg.senderId === currentUserId);

      // For group messages: don't trust __NO_BACKUP__ — the sender key may arrive later.
      // Exception: OWN group messages are never helped by a peer's sender-key arrival;
      // route them into the trusted path (same as DMs) to avoid a per-render server read.
      const isGroupMsg = msg.ciphertext && (() => { try { return JSON.parse(msg.ciphertext!).type === "group_message"; } catch { return false; } })();
      if (!isGroupMsg || isOwn) {
        backupNotFound.add(msg.id);
        reportE2eeError({
          phase: "dm_no_session",
          senderId: msg.senderId,
          deviceId: crypto.deviceId,
          error: `cache=__NO_BACKUP__ msgId=${msg.id} channelId=${msg.channelId} isOwn=${isOwn}`,
        });
        if (isOwn) {
          // 60-second negative cache: skip the server read if we already know it's empty.
          // In-memory guard (survives re-renders); short TTL so a delayed backup write surfaces.
          const ownMissExpiry = ownMissCache.get(msg.id);
          if (ownMissExpiry !== undefined && Date.now() < ownMissExpiry) {
            return { ...msg, plaintext: "[Eigene Nachricht – Verschlüsselung aktiv]" };
          }
          const plain = await tryOwnPlaintextFallback(msg.id);
          if (plain !== null) {
            ownMissCache.delete(msg.id);
            cacheAndBackup(msg.id, plain, msg.channelId);
            return { ...msg, plaintext: plain };
          }
          // Record miss: in-memory (fast) + IDB (survives reload)
          const now = Date.now();
          ownMissCache.set(msg.id, now + 60_000);
          cachePlaintext(msg.id, makeOwnMissMarker(now)).catch(() => {});
          return { ...msg, plaintext: "[Eigene Nachricht – Verschlüsselung aktiv]" };
        }
        const peerPlain = await tryPeerPlaintextFailsafe(msg.channelId, msg.id, "recovery-404");
        if (peerPlain !== null) {
          cacheAndBackup(msg.id, peerPlain, msg.channelId);
          return { ...msg, plaintext: peerPlain, fallbackMode: "plaintext" };
        }
        return { ...msg, plaintext: "[Verschlüsselte Nachricht]" };
      }
      // Peer group message: fall through to retry decryption (sender key may arrive later)
    } else if (isRealPlaintext(idbCached)) {
      ownMessageCache.set(msg.id, idbCached); // promote to memory
      return { ...msg, plaintext: idbCached };
    }
    // Non-real cached value (e.g. expired __OWN_MISS__ sentinel): ignore and
    // fall through to the decrypt cascade below.
  }

  // No ciphertext in message row (multi-device E2EE) and no cache — try backup fallback
  if (!msg.ciphertext) {
    const backupPlaintext = await tryBackupDecrypt(msg);
    if (typeof backupPlaintext === "string") {
      cacheAndBackup(msg.id, backupPlaintext, msg.channelId);
      return { ...msg, plaintext: backupPlaintext };
    }
    // Only persist __NO_BACKUP__ if the server confirmed 404 (not if key wasn't ready)
    if (backupPlaintext !== BACKUP_KEY_NOT_READY && backupNotFound.has(msg.id)) {
      cachePlaintext(msg.id, "__NO_BACKUP__").catch(() => {});
    }
    const { useAuth } = await import("./useAuth");
    const currentUserId = useAuth.getState().user?.id;
    const isOwn = !!(currentUserId && msg.senderId === currentUserId);
    reportE2eeError({
      phase: "dm_no_session",
      senderId: msg.senderId,
      deviceId: crypto.deviceId,
      error: `no_ciphertext msgId=${msg.id} channelId=${msg.channelId} isOwn=${isOwn}`,
    });
    if (isOwn) {
      const plain = await tryOwnPlaintextFallback(msg.id);
      if (plain !== null) {
        cacheAndBackup(msg.id, plain, msg.channelId);
        return { ...msg, plaintext: plain };
      }
      return { ...msg, plaintext: "[Eigene Nachricht – Verschlüsselung aktiv]" };
    }
    // Peer's multi-device message — we need to decrypt from message_recipients.
    // F22 last-resort: server-side plaintext failsafe.
    const noCiphertextReason: CascadeFailReason =
      backupPlaintext === BACKUP_KEY_NOT_READY ? "recovery-decrypt-fail" : "recovery-404";
    const peerPlain = await tryPeerPlaintextFailsafe(msg.channelId, msg.id, noCiphertextReason);
    if (peerPlain !== null) {
      cacheAndBackup(msg.id, peerPlain, msg.channelId);
      return { ...msg, plaintext: peerPlain, fallbackMode: "plaintext" };
    }
    return { ...msg, plaintext: "[Verschlüsselte Nachricht]" };
  }

  // Try pending plaintext cache (own message just sent)
  if (msg.ciphertext) {
    const pending = resolvePendingPlaintext(msg.id, msg.ciphertext, msg.channelId);
    if (pending) {
      return { ...msg, plaintext: pending };
    }
  }

  // Try self-decrypt for own messages (multi-device self-encrypted envelope)
  try {
    const envelope = JSON.parse(msg.ciphertext);
    if (envelope.v === 1 && envelope.selfEncrypted && crypto.deviceKeys) {
      const plaintext = await selfDecrypt(envelope.payload, crypto.deviceKeys.identityKeyPair.curve25519.privateKey);
      if (plaintext) {
        cacheAndBackup(msg.id, plaintext, msg.channelId);
        return { ...msg, plaintext };
      }
    }
  } catch {
    // Not a self-encrypted message or decrypt failed — continue with normal flow
  }

  // Determine if this is an own message (may come from another device in multi-device)
  const { useAuth } = await import("./useAuth");
  const currentUserId = useAuth.getState().user?.id;
  const isOwnMessage = currentUserId && msg.senderId === currentUserId;

  let envelopeType: string | undefined;
  let envelopeSenderDeviceId: string | undefined;
  try {
    const envelope = JSON.parse(msg.ciphertext);
    envelopeType = typeof envelope.type === "string" ? envelope.type : undefined;
    envelopeSenderDeviceId = typeof envelope.senderDeviceId === "string" ? envelope.senderDeviceId : undefined;
    if (envelope.v !== 1) {
      // Unknown envelope version — don't display raw ciphertext
      return { ...msg, plaintext: "[Unbekanntes Nachrichtenformat]" };
    }

    let plaintext: string | null = null;
    if (envelope.type === "group_message") {
      plaintext = await crypto.decryptGroupMessage(msg.senderId, msg.channelId, msg.ciphertext, msg.id);
    } else if (envelope.type === "bot_channel_sender_key_msg") {
      // F16d: bot-channel sender-key message — derive per-message key via HKDF
      // and decrypt with AES-256-GCM. Falls through to null if the key is not
      // yet cached; useChat's pending-group-message machinery will retry once
      // the sender-key-received event fires after the pending distribution arrives.
      // Validate all required fields before entering the decrypt path — malformed
      // envelopes surface as telemetry rather than silent undefined → base64 failures.
      if (!isValidBotChannelSenderKeyEnvelope(envelope)) {
        e2eeLog.warn("bot_channel_sender_key_msg envelope missing required fields", envelope);
        plaintext = null;
      } else {
        plaintext = await crypto.decryptBotChannelSenderKeyMessage(
          msg.channelId,
          msg.senderId,
          envelope,
          msg.id,
        );
      }
    } else if (envelope.type === "sender_key" || envelope.type === "sender_key_x3dh") {
      await crypto.decryptMessage(msg.senderId, msg.ciphertext);
      return null; // Skip display — internal key distribution
    } else {
      // For own messages from another device: the server delivers a device-specific
      // ratchet envelope — decrypt it like any other message (using senderId as peer)
      plaintext = await crypto.decryptMessage(msg.senderId, msg.ciphertext);
    }
    // Cache + backup all successfully decrypted messages
    if (plaintext) {
      cacheAndBackup(msg.id, plaintext, msg.channelId);
      return { ...msg, plaintext };
    }
    // Park undecryptable group messages: if sender key hasn't arrived yet, show
    // a placeholder and retry once onSenderKeyReceived fires for this channel.
    // Also covers bot_channel_sender_key_msg — same key-availability race applies.
    // C5: removed `envelopeSenderDeviceId &&` gate — group_message envelopes missing
    // senderDeviceId now park and self-heal via the C5 receiver fallback in
    // decryptGroupMessage (single-device unambiguous route, or requestSenderKey
    // broadcast). The type-guard (isSenderKeyMsg) is the correct discriminant here.
    const isSenderKeyMsg = envelopeType === "group_message" || envelopeType === "bot_channel_sender_key_msg";
    if (plaintext === null && !isOwnMessage && isSenderKeyMsg) {
      const alreadyPending = pendingGroupMessages.has(msg.id);
      if (!alreadyPending) {
        const giveUpTimer = setTimeout(() => {
          if (!pendingGroupMessages.has(msg.id)) return;
          pendingGroupMessages.delete(msg.id);
          void (async () => {
            // P0: receiver-initiated self-healing — one final key request before
            // falling through to the plaintext failsafe. The initial request fired
            // inside decryptGroupMessage (group_no_key / stale-keyId paths) but the
            // sender may have been transiently offline or the 5s FORCE_MIN_GAP may
            // have collapsed it. Re-emit now; requestSenderKey's existing circuit
            // breaker (FORCE_MAX_RETRIES=3, FORCE_LOCKOUT_MS=5min) prevents storms.
            // We give the key a short window to arrive before escalating to failsafe.
            const { requestSenderKey } = await import("./useCrypto-group");
            requestSenderKey(msg.channelId, msg.senderId, { force: true, skipPending: true });

            // Wait briefly for the key to arrive via sender_key_received → onSenderKeyReceived
            // before attempting a final decrypt. This covers cases where the initial
            // WS request raced with the sender's reconnect (offline → online within
            // the 30s park window). 3s gives enough time for the round-trip without
            // significantly delaying the user-visible fallback.
            await new Promise<void>((resolve) => setTimeout(resolve, 3_000));

            // Final decrypt attempt before escalating to the failsafe.
            // If the key arrived (sender-key-received fired and drained pendingGroupMessages
            // above), the message is no longer in the map and the outer guard already
            // returned. We only reach this branch when the key still hasn't landed.
            const finalDecrypt = await (async () => {
              try {
                const crypto = useCrypto.getState();
                if (crypto.initialized) {
                  return await crypto.decryptGroupMessage(msg.senderId, msg.channelId, msg.ciphertext, msg.id);
                }
              } catch { /* ignore — fall through to backup */ }
              return null;
            })();
            if (typeof finalDecrypt === "string") {
              cacheAndBackup(msg.id, finalDecrypt, msg.channelId);
              useChat.getState().updateMessage(msg.id, msg.channelId, { plaintext: finalDecrypt });
              return;
            }

            const backup = await tryBackupDecrypt(msg);
            if (typeof backup === "string") {
              useChat.getState().updateMessage(msg.id, msg.channelId, { plaintext: backup });
              return;
            }
            const peerPlain = await tryPeerPlaintextFailsafe(
              msg.channelId,
              msg.id,
              "sender-key-miss",
              envelopeSenderDeviceId,
            );
            useChat.getState().updateMessage(msg.id, msg.channelId,
              peerPlain !== null
                ? { plaintext: peerPlain, fallbackMode: "plaintext" }
                : { plaintext: "[Nachricht konnte nicht entschlüsselt werden]" },
            );
          })();
        }, PENDING_GROUP_TIMEOUT_MS);
        pendingGroupMessages.set(msg.id, {
          message: msg, channelId: msg.channelId,
          senderUserId: msg.senderId, senderDeviceId: envelopeSenderDeviceId,
          addedAt: Date.now(), giveUpTimer,
        });
        return { ...msg, plaintext: "[Nachricht wird entschlüsselt…]" };
      }
    }
    // Decryption returned null — try backup fallback. The backup endpoint returns
    // per-user encrypted backups (uploaded on successful decrypt). For group messages
    // from OTHER users, a backup exists if we (or any other device) previously decrypted
    // the message, OR if the sender uploaded it. Rate-limit protection is handled by
    // tryBackupDecrypt's built-in guards (backupNotFound, transient cooldown, inflight dedup).
    const backupFallback1 = await tryBackupDecrypt(msg);
    if (typeof backupFallback1 === "string") {
      cacheAndBackup(msg.id, backupFallback1, msg.channelId);
      return { ...msg, plaintext: backupFallback1 };
    }
    if (backupFallback1 !== BACKUP_KEY_NOT_READY && backupNotFound.has(msg.id)) {
      cachePlaintext(msg.id, "__NO_BACKUP__").catch(() => {});
    }
    reportE2eeError({
      phase: envelopeType === "group_message" ? "group_decrypt_failed" : "dm_decrypt_failed",
      senderId: msg.senderId,
      senderDeviceId: envelopeSenderDeviceId,
      deviceId: crypto.deviceId,
      error: `null_plaintext msgId=${msg.id} channelId=${msg.channelId} envelopeType=${envelopeType ?? "?"} isOwn=${isOwnMessage} backupTried=true`,
    });
    if (envelopeType === "message" && !isOwnMessage) {
      tryDmRecovery(msg.id, msg.channelId, msg.senderId, envelopeSenderDeviceId, crypto.deviceId);
    }
    if (isOwnMessage) {
      const plain = await tryOwnPlaintextFallback(msg.id);
      if (plain !== null) {
        cacheAndBackup(msg.id, plain, msg.channelId);
        return { ...msg, plaintext: plain };
      }
      return { ...msg, plaintext: "[Eigene Nachricht – Verschlüsselung aktiv]" };
    }
    // F22 last-resort: server-side plaintext failsafe for peer messages.
    {
      const nullReason: CascadeFailReason =
        envelopeType === "group_message" || envelopeType === "bot_channel_sender_key_msg"
          ? "sender-key-miss"
          : "ratchet-fail";
      const peerPlain = await tryPeerPlaintextFailsafe(
        msg.channelId,
        msg.id,
        nullReason,
        envelopeSenderDeviceId,
      );
      if (peerPlain !== null) {
        cacheAndBackup(msg.id, peerPlain, msg.channelId);
        return { ...msg, plaintext: peerPlain, fallbackMode: "plaintext" };
      }
    }
    return { ...msg, plaintext: "[Nachricht konnte nicht entschlüsselt werden]" };
  } catch (decryptErr) {
    // Decrypt threw — try backup fallback (same guards as above)
    const backupFallback2 = await tryBackupDecrypt(msg);
    if (typeof backupFallback2 === "string") {
      cacheAndBackup(msg.id, backupFallback2, msg.channelId);
      return { ...msg, plaintext: backupFallback2 };
    }
    if (backupFallback2 !== BACKUP_KEY_NOT_READY && backupNotFound.has(msg.id)) {
      cachePlaintext(msg.id, "__NO_BACKUP__").catch(() => {});
    }
    reportE2eeError({
      phase: envelopeType === "group_message" ? "group_decrypt_failed" : "dm_decrypt_failed",
      senderId: msg.senderId,
      senderDeviceId: envelopeSenderDeviceId,
      deviceId: crypto.deviceId,
      error: `threw msgId=${msg.id} channelId=${msg.channelId} envelopeType=${envelopeType ?? "?"} isOwn=${isOwnMessage} err=${String(decryptErr).slice(0, 200)}`,
    });
    if (envelopeType === "message" && !isOwnMessage) {
      tryDmRecovery(msg.id, msg.channelId, msg.senderId, envelopeSenderDeviceId, crypto.deviceId);
    }
    if (isOwnMessage) {
      const plain = await tryOwnPlaintextFallback(msg.id);
      if (plain !== null) {
        cacheAndBackup(msg.id, plain, msg.channelId);
        return { ...msg, plaintext: plain };
      }
      return { ...msg, plaintext: "[Eigene Nachricht – Verschlüsselung aktiv]" };
    }
    // F22 last-resort: server-side plaintext failsafe for peer messages.
    {
      const threwReason: CascadeFailReason =
        envelopeType === "group_message" || envelopeType === "bot_channel_sender_key_msg"
          ? "sender-key-miss"
          : "ratchet-fail";
      const peerPlain = await tryPeerPlaintextFailsafe(
        msg.channelId,
        msg.id,
        threwReason,
        envelopeSenderDeviceId,
      );
      if (peerPlain !== null) {
        cacheAndBackup(msg.id, peerPlain, msg.channelId);
        return { ...msg, plaintext: peerPlain, fallbackMode: "plaintext" };
      }
    }
    return { ...msg, plaintext: "[Nachricht konnte nicht entschlüsselt werden]" };
  }
}

// ============================================================
// Types
// ============================================================

export interface Channel {
  id: string;
  type: "dm" | "group";
  name: string | null;
  /** Organisation des Kanals — bestimmt, wessen Verbrauch der Balken zeigt (KOL-94) */
  orgId?: string | null;
  description?: string | null;
  avatarUrl?: string | null;
  unreadCount?: number;
  lastMessage?: Message | null;
  members?: ChannelMember[];
}

export interface ChannelMember {
  userId: string;
  displayName: string;
  role: string;
  isBot?: boolean;
}

export interface Reaction {
  emoji: string;
  count: number;
  users: { userId: string; displayName: string }[];
  userReacted: boolean;
}

export interface Message {
  id: string;
  channelId: string;
  senderId: string;
  senderName?: string;
  ciphertext: string;
  contentType: string;
  replyToId?: string | null;
  editedAt?: string | null;
  deletedAt?: string | null;
  createdAt: string;
  // Client-side decrypted content (not persisted)
  plaintext?: string;
  // F22: set to "plaintext" when the message body came from the
  // server-side plaintext failsafe instead of E2EE decrypt. UI shows a
  // transitional-mode badge so the user knows this message was not
  // delivered end-to-end encrypted on this device.
  fallbackMode?: "plaintext";
  // Emoji reactions
  reactions?: Reaction[];
}

export interface TypingUser {
  userId: string;
  displayName: string;
  timestamp: number;
}

// ============================================================
// Store
// ============================================================

// Bot streaming state
export interface BotStream {
  channelId: string;
  botUserId: string;
  content: string;
  isStreaming: boolean;
  // Set when bot_stream_end arrives. Bubble is kept visible (without cursor) until
  // the persisted "message" WS event for this bot arrives — or a fallback timeout
  // expires. Prevents the flicker/disappearance when the persisted message is
  // delayed or never delivered live (F11).
  endedAt?: number;
  // F16: after end-grace expires without persisted message, switch to "finalizing"
  // (last content faded, spinner shown) instead of silently deleting the bubble.
  finalizing?: boolean;
  // F16: after hard-fallback timeout, mark as failed and show a warning so the
  // user knows the response is lost and can retry.
  failed?: boolean;
  // F16g: set when the server could not distribute the sender key for a group
  // channel. No plaintext chunks will arrive; UI shows a static placeholder.
  encryptionSetupFailed?: boolean;
}

// Bot work-in-progress (Fix 2): covers gaps where no stream tokens flow yet
// (e.g. OpenClaw plugin reasoning, tool use, post-processing).
export interface BotTyping {
  channelId: string;
  botUserId: string;
  since: number;
}

interface ChatState {
  // Data
  channels: Channel[];
  activeChannelId: string | null;
  messages: Map<string, Message[]>; // channelId -> messages
  typingUsers: Map<string, TypingUser[]>; // channelId -> typing users
  onlineUsers: Set<string>; // userId set
  lastSeenMap: Map<string, string>; // userId -> ISO timestamp
  // Read receipts: channelId -> { userId -> lastReadMessageId }
  readReceipts: Map<string, Map<string, string>>;
  // Bot streaming: channelId -> stream state
  botStreams: Map<string, BotStream>;
  // Bot typing-indicator state for in-progress requests w/o token stream
  botTyping: Map<string, BotTyping>;
  // KOL-81: ephemeral bot activity ticker ("🔎 Webrecherche: …"); replaced per
  // event, cleared on TTL / stream start. Never persisted.
  botActivity: Map<string, { botUserId: string; text: string; ts: number }>;
  // WebSocket connection state
  connectionState: "connected" | "disconnected" | "reconnecting";

  // Loading states
  isLoadingChannels: boolean;
  isLoadingMessages: boolean;
  hasMoreMessages: boolean;

  // Actions
  setConnectionState: (state: "connected" | "disconnected" | "reconnecting") => void;
  loadChannels: () => Promise<void>;
  createChannel: (type: "dm" | "group", name: string, memberIds: string[]) => Promise<Channel>;
  setActiveChannel: (channelId: string) => void;
  loadMessages: (channelId: string, before?: string) => Promise<void>;
  /** Catch up missed messages for all channels after reconnect */
  catchUpAllChannels: () => Promise<void>;
  /** Clear all cached messages (forces reload from server + IDB cache on next open) */
  clearMessagesCache: () => void;
  addMessage: (message: Message, options?: { fromSelf?: boolean }) => void;
  updateMessage: (messageId: string, channelId: string, updates: Partial<Message>) => void;
  removeMessage: (messageId: string, channelId: string) => void;
  setTyping: (channelId: string, userId: string, displayName: string, isTyping: boolean) => void;
  setUserOnline: (userId: string, online: boolean, lastSeen?: string) => void;
  loadPresence: (userIds: string[]) => Promise<void>;
  updateMessageReaction: (channelId: string, messageId: string, userId: string, emoji: string, added: boolean) => void;
  sendReadReceipt: (channelId: string, messageId: string) => void;
  initWebSocketHandlers: () => () => void;
  decryptAndAddMessage: (message: Message) => Promise<void>;
}

export const useChat = create<ChatState>((set, get) => ({
  channels: [],
  activeChannelId: null,
  messages: new Map(),
  typingUsers: new Map(),
  onlineUsers: new Set(),
  lastSeenMap: new Map(),
  readReceipts: new Map(),
  botStreams: new Map(),
  botTyping: new Map(),
  botActivity: new Map(),
  connectionState: "disconnected",
  isLoadingChannels: false,
  isLoadingMessages: false,
  hasMoreMessages: true,

  setConnectionState: (state) => set({ connectionState: state }),

  loadChannels: async () => {
    set({ isLoadingChannels: true });
    try {
      const data = await api.get<{ channels: Channel[] }>("/channels");
      // Map lastMessage.content → plaintext for sidebar display
      const channels = data.channels.map(ch => {
        if (ch.lastMessage && !ch.lastMessage.plaintext && (ch.lastMessage as unknown as Record<string, unknown>).content) {
          return { ...ch, lastMessage: { ...ch.lastMessage, plaintext: (ch.lastMessage as unknown as Record<string, unknown>).content as string } };
        }
        return ch;
      });
      set({ channels, isLoadingChannels: false });

      // Decrypt lastMessage for sidebar preview (async, updates in background)
      const hasE2EE = channels.some(ch => isE2EEContentType(ch.lastMessage?.contentType));
      if (hasE2EE) {
        await waitForCryptoInit(10000);
        // Cold-load unread reconciliation: the server's getUnreadCount counts every
        // message row after lastReadAt — it cannot know that THIS recipient can't
        // decrypt a given E2EE message. So a channel whose newest message is an
        // undecryptable placeholder shows unread on every fresh load. Detect that
        // here (we already decrypt each channel's lastMessage for the preview) and
        // both clear the local badge AND advance the server read-pointer so it stays
        // cleared across reloads.
        //
        // Gate: unreadCount === 1. With only lastMessage decrypted we can PROVE the
        // whole unread tail is a placeholder only when there's exactly one unread
        // message (which is necessarily lastMessage). For >1 we conservatively leave
        // the badge — marking-read via lastMessage.id would also clear any real
        // unread message sitting below the placeholder.
        const channelsToAck: { channelId: string; messageId: string }[] = [];
        const decryptedChannels = await Promise.all(
          channels.map(async (ch) => {
            if (isE2EEContentType(ch.lastMessage?.contentType)) {
              const decrypted = await decryptSingleMessage(ch.lastMessage);
              if (decrypted && decrypted.plaintext) {
                const placeholderTail = isPlaceholderTail(ch, decrypted.plaintext, !!ch.lastMessage?.id);
                if (placeholderTail) {
                  channelsToAck.push({ channelId: ch.id, messageId: ch.lastMessage!.id });
                }
                const previewText = stripMarkdownForPreview(humanizeFileTokens(decrypted.plaintext));
                return {
                  ...ch,
                  ...(placeholderTail ? { unreadCount: 0 } : {}),
                  lastMessage: { ...decrypted, plaintext: previewText },
                };
              }
            }
            return ch;
          }),
        );
        set({ channels: decryptedChannels });

        // Persist read-pointer for cleared placeholder tails. Use the HTTP endpoint
        // (not WS, which may not be connected yet at channel-list load time) so the
        // server stops resurrecting the badge on the next cold load. Best-effort.
        for (const ack of channelsToAck) {
          api.post(`/channels/${ack.channelId}/read`, { messageId: ack.messageId }).catch(() => {});
        }
      }

      // Update document title with unread count
      const totalUnread = channels.reduce((sum, ch) => sum + (ch.unreadCount ?? 0), 0);
      document.title = totalUnread > 0 ? `(${totalUnread}) KollegePlus` : "KollegePlus";

      // Subscribe to ALL channels for real-time message delivery
      // (ensures messages in non-active channels aren't missed)
      const allChannelIds = channels.map((ch) => ch.id);
      if (allChannelIds.length > 0 && ws.connected) {
        ws.send({ type: "subscribe", channelIds: allChannelIds });
      }

      // Load presence for all DM partners
      const memberIds = new Set<string>();
      for (const ch of data.channels) {
        if (ch.members) {
          for (const m of ch.members) memberIds.add(m.userId);
        }
      }
      if (memberIds.size > 0) {
        get().loadPresence(Array.from(memberIds));
      }

      // Proactive sender key fetch (in case crypto initialized before channels loaded)
      requestMissingSenderKeys(get).catch(() => {});
    } catch {
      set({ isLoadingChannels: false });
    }
  },

  createChannel: async (type, name, memberIds) => {
    const data = await api.post<{ channel: Channel }>("/channels", {
      type,
      name: type === "group" ? name : undefined,
      memberIds,
    });
    set((state) => {
      const exists = state.channels.some((c) => c.id === data.channel.id);
      return {
        channels: exists
          ? state.channels.map((c) => c.id === data.channel.id ? { ...c, ...data.channel } : c)
          : [data.channel, ...state.channels],
      };
    });
    // KOL-103: presence is otherwise only fetched at the initial channel-list
    // load — members of a channel created mid-session showed offline until reload.
    void get().loadPresence(data.channel.members?.map((m) => m.userId) ?? memberIds);
    return data.channel;
  },

  setActiveChannel: (channelId) => {
    // No longer unsubscribe from previous channel — we stay subscribed to all
    // channels so we never miss messages in background chats.
    // Just ensure the new channel is subscribed (idempotent).
    if (channelId && ws.connected) ws.subscribeChannel(channelId);

    // Clear unread count for newly active channel
    set((state) => {
      const channels = state.channels.map((ch) =>
        ch.id === channelId ? { ...ch, unreadCount: 0 } : ch,
      );
      const totalUnread = channels.reduce((sum, ch) => sum + (ch.unreadCount ?? 0), 0);
      updateTitleBadge(totalUnread);
      return { activeChannelId: channelId, channels };
    });

    // Load messages if not cached
    if (channelId && !get().messages.has(channelId)) {
      get().loadMessages(channelId);
    }
  },

  clearMessagesCache: (() => {
    // Coalescing debounce: multiple calls within 500ms collapse to one reload.
    // Prevents cascading reload storms (sender-key-received → re-decrypt →
    // requestSenderKey → new key → sender-key-received → repeat).
    let timer: ReturnType<typeof setTimeout> | null = null;
    return () => {
      if (timer) return; // already scheduled
      timer = setTimeout(() => {
        timer = null;
        const activeId = get().activeChannelId;
        set({ messages: new Map() });
        if (activeId) {
          get().loadMessages(activeId);
        }
      }, 500);
    };
  })(),

  loadMessages: async (channelId, before) => {
    if (!channelId) return;
    set({ isLoadingMessages: true });

    // Instant hydration: paint the cached message list before any network or
    // crypto runs, but only on initial load and only if nothing is in memory
    // yet. The publish() merge below folds the fresh network result into this
    // by ID; its guard prevents regressing real plaintext to placeholders, so
    // the hydrated text does not flicker.
    if (!before && !get().messages.has(channelId)) {
      const cachedList = await loadMessageList(channelId);
      if (cachedList && cachedList.length > 0) {
        set((state) => {
          const updated = new Map(state.messages);
          updated.set(channelId, cachedList as Message[]);
          return { messages: updated };
        });
      }
    }

    try {
      // ponytail: still re-fetches first page; delta-fetch via ?after if network cost matters
      const params = before ? `?before=${before}&limit=50` : "?limit=50";
      const data = await api.get<{ messages: Message[]; readReceipts?: Record<string, string>; sessionResets?: string[] }>(
        `/channels/${channelId}/messages${params}`,
      );

      // Handle pending session resets (HTTP polling fallback for when WS event was missed).
      // Wrap the whole loop in withBusy so auto-reload cannot fire between iterations,
      // which would leave some sessions deleted and others stale.
      if (data.sessionResets && data.sessionResets.length > 0) {
        const crypto = useCrypto.getState();
        if (crypto.initialized) {
          await withBusy(async () => {
            for (const peerId of data.sessionResets!) {
              e2eeLog.warn("Session reset received via HTTP polling", { peerId });
              await crypto.deleteSessionForPeer(peerId).catch(() => {});
            }
          });
        }
      }

      // Initialize read receipts from server
      if (data.readReceipts && !before) {
        set((state) => {
          const updated = new Map(state.readReceipts);
          const channelMap = new Map<string, string>();
          for (const [userId, msgId] of Object.entries(data.readReceipts!)) {
            channelMap.set(userId, msgId);
          }
          updated.set(channelId, channelMap);
          return { readReceipts: updated };
        });
      }

      // Decrypt loaded messages SEQUENTIALLY in chronological order.
      // This is critical: the first message from a peer may be an X3DH initial
      // handshake that establishes the session. Subsequent messages are regular
      // ratchet messages that need that session. Parallel or reverse-order
      // decryption would fail because the session doesn't exist yet.
      const sorted = [...data.messages].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );

      // ponytail: one batch IDB read for all message plaintexts instead of N
      // per-message getCachedPlaintext round-trips inside the decrypt cascade.
      const batchCache = await getCachedPlaintexts(sorted.map((m) => m.id));

      // Publish helper: write a message set into the store. Called twice on an
      // initial load — first with placeholders (instant render), then with the
      // decrypted result — so the list appears immediately instead of only
      // after the whole sequential decrypt loop finishes.
      const publish = (msgs: Message[], loading: boolean) => set((state) => {
        const existing = state.messages.get(channelId) ?? [];
        let newMessages: Message[];

        if (before) {
          // Pagination: prepend older messages
          newMessages = [...msgs, ...existing];
        } else if (existing.length > 0) {
          // Refresh / placeholder→decrypted upgrade: merge by ID, keeping older
          // history. Never regress an already-decrypted plaintext to a placeholder.
          newMessages = mergeMessagesPreservingPlaintext(existing, msgs).sort(
            (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
          );
        } else {
          // Initial load
          newMessages = msgs;
        }

        const updated = new Map(state.messages);
        updated.set(channelId, newMessages);

        const base = {
          messages: updated,
          isLoadingMessages: loading,
          hasMoreMessages: before ? msgs.length >= 50 : true,
        };

        // Sidebar sync: pagination never changes the newest message; leave it.
        if (before || newMessages.length === 0) return base;
        const latest = [...newMessages].reverse().find((m) => !m.deletedAt) ?? newMessages[newMessages.length - 1];
        const channels = state.channels.map((ch) =>
          ch.id === channelId ? { ...ch, lastMessage: buildSidebarLastMessage(latest) } : ch,
        );
        return { ...base, channels };
      });

      // Initial/refresh load: render placeholders immediately so the message
      // list shows up instead of the empty state during the decrypt loop.
      // Skipped for pagination (`before`) — that prepends, and a placeholder
      // prepend followed by a decrypted prepend would duplicate the page.
      if (!before) {
        const placeholders = sorted.map((m) =>
          isE2EEContentType(m.contentType)
            ? { ...m, plaintext: "[Nachricht wird entschlüsselt…]" }
            : { ...m, plaintext: m.ciphertext },
        );
        publish(placeholders, true);
      }

      // ── Central Crypto Ready Gate ──
      // Wait for E2EE crypto to be initialized before attempting decryption.
      // Without this, messages load before keys are ready, causing permanent
      // "[Verschlüsselte Nachricht]" placeholders and false __NO_BACKUP__ markers.
      // Only block on crypto init if there are E2EE messages we cannot already
      // serve from the plaintext cache — cached messages need no live crypto.
      // Runs AFTER the placeholder publish so the list renders instantly even
      // while crypto initializes (otherwise this await re-delays first paint).
      const needsCrypto = sorted.some(
        (m) => isE2EEContentType(m.contentType) && !m.deletedAt &&
          !isRealPlaintext(batchCache.get(m.id) ?? ""),
      );
      if (needsCrypto) {
        const ready = await waitForCryptoInit(10000);
        if (!ready) {
          e2eeLog.warn("[chat] Crypto init timed out after 10s — messages may show as encrypted");
        }
      }

      const decryptedMessages: Message[] = [];
      const failedE2eeMessages: Message[] = [];
      for (const msg of sorted) {
        // Short-circuit: serve already-decrypted messages straight from the
        // batch cache, skipping the per-message IDB read inside the cascade.
        // X3DH handshake-ordering does not apply here — a cached message's
        // ratchet was already advanced+persisted on its first decrypt.
        const cachedPt = batchCache.get(msg.id);
        if (cachedPt && isRealPlaintext(cachedPt) && !msg.deletedAt) {
          decryptedMessages.push({ ...msg, plaintext: cachedPt });
          continue;
        }
        const result = await decryptSingleMessage(msg);
        if (result) {
          // Track if this E2EE message failed to decrypt (session was likely broken & deleted)
          if (isE2EEContentType(msg.contentType) && msg.ciphertext &&
              (result.plaintext === "[Nachricht konnte nicht entschlüsselt werden]" ||
               result.plaintext === "[Verschlüsselte Nachricht]")) {
            failedE2eeMessages.push(msg);
          }
          decryptedMessages.push(result);
        }
      }

      // Auto-retry: if some E2EE messages failed, the broken session was deleted
      // during the first pass. Now retry those messages — if one of them is an
      // X3DH initial message, it can establish a fresh session and the rest can
      // be decrypted using the new session.
      if (failedE2eeMessages.length > 0) {
        e2eeLog.log(`Retrying ${failedE2eeMessages.length} failed E2EE messages after session cleanup`);
        for (const msg of failedE2eeMessages) {
          const retryResult = await decryptSingleMessage(msg);
          if (retryResult && retryResult.plaintext !== "[Nachricht konnte nicht entschlüsselt werden]" &&
              retryResult.plaintext !== "[Verschlüsselte Nachricht]") {
            // Replace the failed message with the successfully decrypted one
            const idx = decryptedMessages.findIndex((m) => m.id === msg.id);
            if (idx !== -1) decryptedMessages[idx] = retryResult;
            e2eeLog.log(`✅ Retry succeeded for message ${msg.id}`);
          }
        }
      }

      // Final publish: replace placeholders with the decrypted result. The
      // merge guard inside publish upgrades placeholder→real and never regresses
      // an already-decrypted plaintext (late-decrypt reload flicker protection).
      publish(decryptedMessages, false);

      // Persist the freshest page for instant hydration on next reload. Only
      // store successfully-decrypted, non-deleted messages — never placeholders
      // or undecryptable ones. void: don't block the load on the cache write.
      if (!before) {
        const finalList = (get().messages.get(channelId) ?? []).filter(
          (m) => isRealPlaintext(m.plaintext) && !m.deletedAt,
        );
        void saveMessageList(channelId, finalList);
      }
    } catch {
      set({ isLoadingMessages: false });
    }
  },

  catchUpAllChannels: async () => {
    const state = get();
    const channels = state.channels;
    if (channels.length === 0) return;

    // For each channel, find the newest message timestamp and fetch anything newer
    for (const channel of channels) {
      try {
        const existing = state.messages.get(channel.id) ?? [];
        if (existing.length === 0) continue; // No cached messages → loadMessages will handle it

        // Find the newest message timestamp
        const newest = existing[existing.length - 1];
        if (!newest) continue;

        const afterParam = new Date(newest.createdAt).toISOString();
        const data = await api.get<{ messages: Message[] }>(
          `/channels/${channel.id}/messages?after=${encodeURIComponent(afterParam)}&limit=100`,
        );

        if (!data.messages || data.messages.length === 0) continue;

        // Decrypt new messages
        const decrypted: Message[] = [];
        for (const msg of data.messages) {
          const result = await decryptSingleMessage(msg);
          if (result) decrypted.push(result);
        }

        if (decrypted.length === 0) continue;

        // Own messages from the user's OTHER devices must not count as unread —
        // senderId is a userId, so compare against useAuth.user.id (the previous
        // check used useCrypto.deviceId, which is a different namespace and so
        // the predicate could never match → own messages always counted).
        const { useAuth } = await import("./useAuth");
        const currentUserId = useAuth.getState().user?.id;

        // Merge into existing messages
        set((s) => {
          const current = s.messages.get(channel.id) ?? [];
          const sorted = mergeMessagesPreservingPlaintext(current, decrypted).sort(
            (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
          );
          const updated = new Map(s.messages);
          updated.set(channel.id, sorted);

          // Update unread count for non-active channels
          const isActive = channel.id === s.activeChannelId;
          // Count only peers' freshly-decrypted, readable messages. Skip undecryptable
          // placeholders so a recurring decrypt error doesn't keep re-marking unread.
          // Dedup against messages already in store at set() time: WS-push messages that
          // arrived between the catch-up HTTP fetch and this set() call are already in
          // `current` (and already bumped unread via addMessage). Only count IDs that
          // weren't in the store yet to prevent double-bumping the unread badge.
          const existingIds = new Set(current.map((m) => m.id));
          const newCount = countRealUnread(
            decrypted.filter((m) => !existingIds.has(m.id)),
            currentUserId,
          );
          const updatedChannels = s.channels.map(ch =>
            ch.id === channel.id && !isActive
              ? { ...ch, unreadCount: (ch.unreadCount ?? 0) + newCount, lastMessage: sorted[sorted.length - 1] }
              : ch.id === channel.id
                ? { ...ch, lastMessage: sorted[sorted.length - 1] }
                : ch
          );

          return { messages: updated, channels: updatedChannels };
        });

        e2eeLog.log(`Catch-up: ${decrypted.length} new messages for channel ${channel.id}`);
      } catch (err) {
        e2eeLog.warn(`Catch-up failed for channel ${channel.id}:`, err);
      }
    }

    // Update title badge
    const totalUnread = get().channels.reduce((sum, ch) => sum + (ch.unreadCount ?? 0), 0);
    updateTitleBadge(totalUnread);
  },

  addMessage: (message, options) => {
    set((state) => {
      const existing = state.messages.get(message.channelId) ?? [];

      // Duplicate check: skip if same message ID already in list
      if (existing.some((m) => m.id === message.id)) {
        return state;
      }

      const updated = new Map(state.messages);
      updated.set(message.channelId, [...existing, message]);

      const isActiveChannel = message.channelId === state.activeChannelId;
      // Own messages from another device of the same user: history still
      // appears in the chat view, but no unread bump, no badge, no browser
      // notification (the user has already seen it — they wrote it).
      const fromSelf = options?.fromSelf === true;

      const sidebarMessage = buildSidebarLastMessage(message);

      // Update channel last message and unread count
      const channels = state.channels.map((ch) =>
        ch.id === message.channelId
          ? {
            ...ch,
            lastMessage: sidebarMessage,
            // Undecryptable messages carry a status placeholder plaintext
            // ("[Verschlüsselte Nachricht]" etc.). They must NOT bump unread —
            // otherwise every recurring decrypt error re-marks the channel unread
            // even though nothing readable arrived. (placeholder → real recovery
            // happens via updateMessage, which deliberately leaves unread alone.)
            unreadCount: bumpUnread(ch.unreadCount, message, { isActiveChannel, fromSelf }),
          }
          : ch,
      );

      // Browser notification for messages in non-active channels
      if (!isActiveChannel && !fromSelf) {
        const channel = state.channels.find((ch) => ch.id === message.channelId);
        let channelName = channel?.name ?? "Direktnachricht";
        if (channel?.type === "dm" && channel.members) {
          const partner = channel.members.find((m) => m.userId === message.senderId);
          if (partner?.displayName) channelName = partner.displayName;
        }
        const senderName = message.senderName ?? "Jemand";
        // Use decrypted plaintext for notification, fallback to generic text
        const body = message.plaintext?.substring(0, 100)
          ?? (isE2EEContentType(message.contentType) ? "Neue verschlüsselte Nachricht" : message.ciphertext?.substring(0, 100))
          ?? "Neue Nachricht";
        showNotification(`${senderName} in ${channelName}`, body);
      }

      // Update tab title with total unread
      const totalUnread = channels.reduce((sum, ch) => sum + (ch.unreadCount ?? 0), 0);
      updateTitleBadge(totalUnread);

      return { messages: updated, channels };
    });
  },

  updateMessage: (messageId, channelId, updates) => {
    set((state) => {
      const msgs = state.messages.get(channelId);
      if (!msgs) return state;

      const updated = new Map(state.messages);
      updated.set(
        channelId,
        msgs.map((m) => (m.id === messageId ? { ...m, ...updates } : m)),
      );

      // Keep sidebar preview in sync: when a late-decrypt path (sender-key drain,
      // backup fallback, dm_recover_replaced) replaces a placeholder plaintext,
      // the channel's lastMessage row would otherwise remain on the placeholder.
      const channelNeedsSync = state.channels.some(
        (ch) => ch.id === channelId && ch.lastMessage?.id === messageId,
      );
      if (!channelNeedsSync) {
        return { messages: updated };
      }
      const channels = state.channels.map((ch) =>
        ch.id === channelId && ch.lastMessage?.id === messageId
          ? { ...ch, lastMessage: buildSidebarLastMessage({ ...ch.lastMessage, ...updates }) }
          : ch,
      );
      return { messages: updated, channels };
    });
  },

  removeMessage: (messageId, channelId) => {
    set((state) => {
      const msgs = state.messages.get(channelId);
      if (!msgs) return state;

      const updated = new Map(state.messages);
      updated.set(
        channelId,
        msgs.map((m) =>
          m.id === messageId ? { ...m, deletedAt: new Date().toISOString() } : m,
        ),
      );
      return { messages: updated };
    });
  },

  setTyping: (channelId, userId, displayName, isTyping) => {
    set((state) => {
      const updated = new Map(state.typingUsers);
      const current = updated.get(channelId) ?? [];

      if (isTyping) {
        const exists = current.find((u) => u.userId === userId);
        if (exists) {
          exists.timestamp = Date.now();
        } else {
          updated.set(channelId, [
            ...current,
            { userId, displayName, timestamp: Date.now() },
          ]);
        }
      } else {
        updated.set(
          channelId,
          current.filter((u) => u.userId !== userId),
        );
      }

      return { typingUsers: updated };
    });
  },

  setUserOnline: (userId, online, lastSeen?: string) => {
    set((state) => {
      const updated = new Set(state.onlineUsers);
      const seenMap = new Map(state.lastSeenMap);
      if (online) {
        updated.add(userId);
      } else {
        updated.delete(userId);
        if (lastSeen) seenMap.set(userId, lastSeen);
        else seenMap.set(userId, new Date().toISOString());
      }
      return { onlineUsers: updated, lastSeenMap: seenMap };
    });
  },

  loadPresence: async (userIds) => {
    if (userIds.length === 0) return;
    try {
      const data = await api.post<{ presence: Record<string, { online: boolean }> }>(
        "/users/presence",
        { userIds },
      );
      set((state) => {
        const updated = new Set(state.onlineUsers);
        for (const [uid, p] of Object.entries(data.presence)) {
          if (p.online) updated.add(uid);
          else updated.delete(uid);
        }
        return { onlineUsers: updated };
      });
    } catch {
      // Presence is best-effort
    }
  },

  updateMessageReaction: (channelId, messageId, userId, emoji, added) => {
    set((state) => {
      const msgs = state.messages.get(channelId);
      if (!msgs) return state;

      const updated = new Map(state.messages);
      updated.set(
        channelId,
        msgs.map((m) => {
          if (m.id !== messageId) return m;
          const reactions = [...(m.reactions ?? [])];
          const existing = reactions.find((r) => r.emoji === emoji);

          if (added) {
            if (existing) {
              existing.count++;
              existing.users.push({ userId, displayName: "" });
              // Check if it's the current user reacting
              existing.userReacted = existing.userReacted || false; // will be set by caller context
            } else {
              reactions.push({
                emoji,
                count: 1,
                users: [{ userId, displayName: "" }],
                userReacted: false,
              });
            }
          } else {
            if (existing) {
              existing.count--;
              existing.users = existing.users.filter((u) => u.userId !== userId);
              if (existing.count <= 0) {
                const idx = reactions.indexOf(existing);
                if (idx >= 0) reactions.splice(idx, 1);
              }
            }
          }

          return { ...m, reactions };
        }),
      );
      return { messages: updated };
    });
  },

  sendReadReceipt: (channelId, messageId) => {
    ws.sendReadReceipt(channelId, messageId);
  },

  decryptAndAddMessage: async (message: Message) => {
    // __system__ messages are internal envelopes (sender_key_x3dh distribution relayed
    // via ws.on("message")). They are NEVER user-facing. Still, we must process the
    // envelope to advance ratchets / absorb keys — but without plinging, without
    // addMessage, without UI side-effects.
    // Fail-safe: even if decryptSingleMessage's catch block returns a truthy placeholder
    // (e.g. when a duplicate/stale sender_key envelope throws inside decryptMessage),
    // the __system__ channel ensures it never reaches the user.
    if (message.channelId === "__system__") {
      // Best-effort: try to process the key envelope so ratchets/sender-keys advance.
      // Ignore result — no UI, no pling.
      await decryptSingleMessage(message).catch(() => {});
      return;
    }
    // Wait for crypto init before decrypting — prevents storing undecrypted placeholders
    if (isE2EEContentType(message.contentType) && !useCrypto.getState().initialized) {
      await waitForCryptoInit(10000);
    }
    const result = await decryptSingleMessage(message);
    if (result) {
      // Detect own-from-other-device BEFORE addMessage so the unread/badge
      // path can also skip (sound suppression below already handles the pling).
      const { useAuth } = await import("./useAuth");
      const currentUserId = useAuth.getState().user?.id;
      const isOwnMessage = Boolean(currentUserId && result.senderId === currentUserId);

      get().addMessage(result, { fromSelf: isOwnMessage });

      // Play notification sound for messages from OTHERS only (own messages
      // from another device would otherwise pling the laptop every time the
      // phone sends). Skip when the user is actively in the channel AND the
      // window is focused.
      if (isOwnMessage) return;
      const isActiveChannel = result.channelId === get().activeChannelId;
      if (!isActiveChannel || !document.hasFocus()) {
        playNotificationSound();
      }
    }
  },

  initWebSocketHandlers: () => {
    const unsubs: Array<() => void> = [];

    // F23b: track which channelIds we've already emitted request_channel_redistribute
    // for in this session so we don't re-emit on every reconnect.  The server-side
    // rate-limit (5 min per user+channel) is the hard cap; this is a cheap in-memory
    // de-dupe so we don't even send the frame on a quick disconnect/reconnect cycle.
    const redistributeRequestedThisSession = new Set<string>();

    // ── Bot streaming timers (referenced by ws.on("message") + bot_stream_* handlers) ──
    // - botStreamTimeouts: safety timer between start and end (kills stuck bubbles)
    // - botStreamCleanupTimers: after end, holds the bubble visible until the persisted
    //   "message" arrives or this timeout fires (whichever first). Stops the flicker
    //   where end clears the bubble before the persisted msg WS event lands (F11).
    const botStreamTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
    const botStreamCleanupTimers = new Map<string, ReturnType<typeof setTimeout>>();
    const botStreamHardFailTimers = new Map<string, ReturnType<typeof setTimeout>>();
    const botTypingTimers = new Map<string, ReturnType<typeof setTimeout>>();
// KOL-81: activity ticker TTL — an event with no successor disappears after 30s.
const BOT_ACTIVITY_TTL_MS = 30_000;
const botActivityTimers = new Map<string, ReturnType<typeof setTimeout>>();
    // F16: extended to cover OpenClaw-plugin / Codi reply paths where reasoning +
    // tool-use can run well past 60s between bot_stream_start and the persisted msg.
    const BOT_STREAM_TIMEOUT_MS = 300_000; // 5 min — start to end (was 60s)
    const BOT_STREAM_END_GRACE_MS = 60_000; // 60 s — end to persisted message arrival (was 5s)
    const BOT_STREAM_HARD_FAIL_MS = 600_000; // 10 min — give up and show error state
    const BOT_TYPING_TIMEOUT_MS = 600_000; // 10 min — safety drop for stuck typing flag

    // Re-decrypt messages after E2EE crypto is initialized.
    // This handles the race condition where loadMessages runs before
    // crypto.initialize() completes, resulting in "[Verschlüsselte Nachricht]"
    // placeholders that never get re-decrypted.
    let cryptoWasInitialized = useCrypto.getState().initialized;
    unsubs.push(
      useCrypto.subscribe((state) => {
        if (state.initialized && !cryptoWasInitialized) {
          cryptoWasInitialized = true;
          // Clear cached messages and reload — they'll now decrypt properly
          const activeId = get().activeChannelId;
          if (activeId) {
            e2eeLog.log("[chat] Crypto initialized — re-decrypting messages for active channel");
            get().clearMessagesCache();
          }

          // Bulk-fetch all pending sender keys so group messages decrypt immediately
          (async () => {
            try {
              const { keys } = await api.get<{ keys: Array<{ id: string; channelId: string; senderId: string; envelope: string }> }>(
                "/users/keys/pending",
              );
              if (keys.length > 0) {
                e2eeLog.log("[chat] Bulk-processing", keys.length, "pending sender keys after crypto init");
                const crypto = useCrypto.getState();
                const processedChannels = new Set<string>();
                // Per-envelope ACK: only delete rows we actually decrypted; leave failures for retry.
                const successfulIds: string[] = [];
                for (const { id, senderId, envelope, channelId } of keys) {
                  try {
                    await crypto.decryptMessage(senderId, envelope);
                    processedChannels.add(channelId);
                    successfulIds.push(id);
                  } catch (err) {
                    e2eeLog.warn("[chat] Failed to process pending sender key from", senderId, err);
                  }
                }
                if (successfulIds.length > 0) {
                  try { await api.post(`/users/keys/pending/ack`, { ids: successfulIds }); } catch {}
                }
                // Trigger re-decrypt for affected channels
                if (processedChannels.size > 0) {
                  get().clearMessagesCache();
                }
              }

              // Proactive: request missing sender keys for all group channels
              await requestMissingSenderKeys(get);
            } catch (err) {
              e2eeLog.warn("[chat] Failed to bulk-fetch pending sender keys:", err);
            }
          })();
        }
      }),
    );

    // Re-decrypt messages when a new sender key arrives (e.g. after requestSenderKey).
    // One recovery round typically dispatches multiple events (one per pending envelope
    // processed + one per batch completion). Each event would otherwise trigger a full
    // clearMessagesCache → refetch → re-render, producing visible flicker when a peer
    // sends a message whose key we don't yet have (common in multi-device scenarios
    // where own other devices redistribute keys). Debounce so rapid-fire bursts collapse
    // to a single reload.
    //
    // H4: channel-agnostic recovery. Background channels also need re-decryption when
    // their sender keys arrive late — previously, undecryptable placeholders stuck around
    // until user navigated to the channel *and* triggered a manual reload. Now:
    //   - active channel  → debounced clearMessagesCache (keeps UX smooth)
    //   - other channels  → drop their cached messages + background loadMessages, so
    //                       the user sees fresh plaintext the moment they navigate there.
    const senderKeyReloadTimers = new Map<string, ReturnType<typeof setTimeout>>();
    const onSenderKeyReceived = (e: Event) => {
      const { channelId } = (e as CustomEvent).detail;
      if (!channelId) return;

      // P2: A fresh sender key arrived — clear failsafe miss markers so messages
      // that hit the failsafe before the key was available can retry decryption.
      // This must happen BEFORE pendingGroupMessages are drained, so the drain's
      // decryptSingleMessage calls don't re-hit the now-stale "no-failsafe" guard.
      clearPeerPlaintextFailsafeMarkers(channelId);

      // F9: Drain parked group messages for this channel.
      const now = Date.now();
      for (const [msgId, entry] of pendingGroupMessages) {
        if (entry.channelId !== channelId) continue;
        clearTimeout(entry.giveUpTimer);
        pendingGroupMessages.delete(msgId);
        const expired = now - entry.addedAt > PENDING_GROUP_TIMEOUT_MS;
        if (expired) {
          get().updateMessage(msgId, channelId, { plaintext: "[Nachricht konnte nicht entschlüsselt werden]" });
          continue;
        }
        void decryptSingleMessage(entry.message).then((result) => {
          if (result && result.plaintext && !result.plaintext.startsWith("[Nachricht wird")) {
            get().updateMessage(msgId, channelId, {
              plaintext: result.plaintext,
              ...(result.fallbackMode ? { fallbackMode: result.fallbackMode } : {}),
            });
          } else {
            get().updateMessage(msgId, channelId, { plaintext: "[Nachricht konnte nicht entschlüsselt werden]" });
          }
        }).catch(() => {
          get().updateMessage(msgId, channelId, { plaintext: "[Nachricht konnte nicht entschlüsselt werden]" });
        });
      }

      const existing = senderKeyReloadTimers.get(channelId);
      if (existing) clearTimeout(existing);
      const timer = setTimeout(() => {
        senderKeyReloadTimers.delete(channelId);
        const activeId = get().activeChannelId;
        if (channelId === activeId) {
          e2eeLog.log("[chat] New sender key(s) received for active channel — reloading messages (debounced)");
          get().clearMessagesCache();
        } else {
          // Background channel: drop its cached messages and refetch silently.
          // clearMessagesCache() only reloads the active channel — for inactive
          // ones we must surgically invalidate so stuck "undecryptable" placeholders
          // get replaced next time the user visits.
          e2eeLog.log("[chat] New sender key(s) received for background channel", channelId, "— refetching");
          set((state) => {
            const next = new Map(state.messages);
            next.delete(channelId);
            return { messages: next };
          });
          get().loadMessages(channelId);
        }
      }, 250);
      senderKeyReloadTimers.set(channelId, timer);
    };
    window.addEventListener("sender-key-received", onSenderKeyReceived);
    unsubs.push(() => {
      window.removeEventListener("sender-key-received", onSenderKeyReceived);
      for (const t of senderKeyReloadTimers.values()) clearTimeout(t);
      senderKeyReloadTimers.clear();
      for (const entry of pendingGroupMessages.values()) clearTimeout(entry.giveUpTimer);
      pendingGroupMessages.clear();
    });

    // F22c: Re-decrypt when a peer delivers a previously-missing channelRecoveryKey.
    // Uses the same debounced reload pattern as sender-key-received — the recovery
    // key is channel-wide (not per-message), so a full channel reload is correct.
    const onChannelRecoveryKeyReceived = (e: Event) => {
      const { channelId } = (e as CustomEvent<{ channelId: string }>).detail;
      if (!channelId) return;
      e2eeLog.log("[chat] F22c: channel-recovery-key-received for channel", channelId, "— triggering re-decrypt");
      const activeId = get().activeChannelId;
      if (channelId === activeId) {
        get().clearMessagesCache();
      } else {
        set((state) => {
          const next = new Map(state.messages);
          next.delete(channelId);
          return { messages: next };
        });
        get().loadMessages(channelId);
      }
    };
    window.addEventListener("channel-recovery-key-received", onChannelRecoveryKeyReceived);
    unsubs.push(() => window.removeEventListener("channel-recovery-key-received", onChannelRecoveryKeyReceived));

    // Bug D: when user_backup_key arrives from peer device, clear BACKUP_KEY_NOT_READY
    // guards and reload messages so per_user backup decrypts can be re-attempted.
    // Debounced — a single reload covers all channels that had stuck backups.
    let userBackupKeyReloadTimer: ReturnType<typeof setTimeout> | null = null;
    const onUserBackupKeyReceived = () => {
      if (userBackupKeyReloadTimer) clearTimeout(userBackupKeyReloadTimer);
      userBackupKeyReloadTimer = setTimeout(() => {
        userBackupKeyReloadTimer = null;
        e2eeLog.info("[Bug D] user-backup-key-received — reloading messages cache");
        // Clear the BACKUP_KEY_NOT_READY guard sets so tryBackupDecrypt retries.
        // backupNotFound tracks 404s only (no backup row), NOT missing key —
        // those are returned as BACKUP_KEY_NOT_READY without adding to the set.
        // So we only need to clear the active channel's message cache so messages
        // that returned BACKUP_KEY_NOT_READY get a fresh decrypt pass.
        get().clearMessagesCache();
      }, 300);
    };
    window.addEventListener("user-backup-key-received", onUserBackupKeyReceived);
    unsubs.push(() => {
      window.removeEventListener("user-backup-key-received", onUserBackupKeyReceived);
      if (userBackupKeyReloadTimer) clearTimeout(userBackupKeyReloadTimer);
    });

    // Track WebSocket connection state
    unsubs.push(
      ws.on("_connected", () => {
        set({ connectionState: "connected" });

        // Clear any stale bot streaming states after reconnect
        // (stream_end may have been missed during disconnect)
        for (const [chId, timer] of botStreamTimeouts) {
          clearTimeout(timer);
          botStreamTimeouts.delete(chId);
        }
        for (const [chId, timer] of botStreamCleanupTimers) {
          clearTimeout(timer);
          botStreamCleanupTimers.delete(chId);
        }
        for (const [chId, timer] of botStreamHardFailTimers) {
          clearTimeout(timer);
          botStreamHardFailTimers.delete(chId);
        }
        for (const [chId, timer] of botTypingTimers) {
          clearTimeout(timer);
          botTypingTimers.delete(chId);
        }
        set({ botStreams: new Map(), botTyping: new Map() });

        // Subscribe to ALL known channels so we never miss messages
        // (not just the active one — messages in background chats were being lost)
        const allChannelIds = get().channels.map((ch) => ch.id);
        if (allChannelIds.length > 0) {
          ws.send({ type: "subscribe", channelIds: allChannelIds });
        }

        // Catch-up: fetch missed messages for ALL channels (not just active)
        // Delayed to give crypto time to init after reconnect
        const activeId = get().activeChannelId;
        if (activeId) {
          get().loadMessages(activeId); // Immediate load for active channel
        }
        setTimeout(() => {
          get().catchUpAllChannels();
        }, 2000);

        // F23b: Zero-Active-Devices Redistribute — after every WS connect, check
        // whether we're missing sender keys for group channels.  If so, ask the
        // server to fan-out proxy_redistribute_request to all other members so
        // they re-distribute their sender key + recovery key to us.
        //
        // Detection heuristic: we have neither our own sender key (loadSenderKey)
        // nor any peer sender keys (loadPeerSenderKeysForChannel) for the channel.
        // This matches the "re-login after all devices inactivated" symptom
        // exactly.  The request is idempotent on the server side (member-side
        // distributeSenderKey does an UPSERT into pending_sender_keys).
        (async () => {
          try {
            const { loadSenderKey, loadPeerSenderKeysForChannel } = await import("../lib/crypto-storage");
            const { reportE2eeError } = await import("../lib/e2ee-telemetry");
            const groupChannels = get().channels.filter(
              (ch) => (ch as { type?: string }).type === "group" &&
                      ((ch as { members?: unknown[] }).members?.length ?? 0) > 1,
            );
            for (const ch of groupChannels) {
              if (redistributeRequestedThisSession.has(ch.id)) continue;
              const sk = await loadSenderKey(ch.id);
              if (sk) continue; // we have our own key — no need to request
              const peerKeys = await loadPeerSenderKeysForChannel(ch.id);
              if (peerKeys.length > 0) continue; // we have peer keys — fine
              // No keys at all: emit redistribute request.
              redistributeRequestedThisSession.add(ch.id);
              ws.send({ type: "request_channel_redistribute", channelId: ch.id });
              reportE2eeError({
                phase: "redistribute_request_sent",
                error: `channelId=${ch.id}`,
              });
            }
          } catch (err) {
            e2eeLog.warn("[chat] F23b: redistribute check failed on connect", err);
          }
        })();
      }),
    );
    unsubs.push(
      ws.on("_disconnected", () => {
        set({ connectionState: ws.connectionState });
      }),
    );
    unsubs.push(
      ws.on("_reconnect_failed", () => {
        set({ connectionState: "disconnected" });
      }),
    );

    unsubs.push(
      ws.on("message", (data) => {
        // Backend sends: { type: "message", channelId, message: {...} }
        const msg = data.message as Message | undefined;
        if (!msg) return;

        // Defensive: the WS frame wrapper ALWAYS carries the authoritative
        // channelId, but some delivery paths handed us an inner message object
        // whose channelId was undefined. That undefined then propagated into the
        // decrypt cascade (telemetry: dm_decrypt_failed channelId=undefined) and
        // crashed tryDmRecovery on `channelId.startsWith`. Backfill from the
        // wrapper so the whole pipeline always has a channelId.
        const wrapperChannelId = (data as { channelId?: string }).channelId;
        if (!msg.channelId && typeof wrapperChannelId === "string") {
          msg.channelId = wrapperChannelId;
        }

        // F11: if a bot stream is held open for this channel and the arriving message
        // is from that bot, clear the bubble immediately — the persisted message
        // takes over without flicker. Done before chaining decrypt so it's
        // synchronous with the WS frame.
        const stream = get().botStreams.get(msg.channelId);
        if (stream && stream.botUserId === msg.senderId) {
          const cleanupTimer = botStreamCleanupTimers.get(msg.channelId);
          if (cleanupTimer) {
            clearTimeout(cleanupTimer);
            botStreamCleanupTimers.delete(msg.channelId);
          }
          const safetyTimer = botStreamTimeouts.get(msg.channelId);
          if (safetyTimer) {
            clearTimeout(safetyTimer);
            botStreamTimeouts.delete(msg.channelId);
          }
          const failTimer = botStreamHardFailTimers.get(msg.channelId);
          if (failTimer) {
            clearTimeout(failTimer);
            botStreamHardFailTimers.delete(msg.channelId);
          }
          set((state) => {
            const updated = new Map(state.botStreams);
            updated.delete(msg.channelId);
            return { botStreams: updated };
          });
        }
        // F16 Fix 2: persisted bot message also ends any standalone typing indicator.
        const typing = get().botTyping.get(msg.channelId);
        if (typing && typing.botUserId === msg.senderId) {
          const typingTimer = botTypingTimers.get(msg.channelId);
          if (typingTimer) {
            clearTimeout(typingTimer);
            botTypingTimers.delete(msg.channelId);
          }
          set((state) => {
            const updated = new Map(state.botTyping);
            updated.delete(msg.channelId);
            return { botTyping: updated };
          });
        }

        // Chain onto incomingMessageChain so frames are processed strictly in
        // order. Critical for the distribute-envelope → group-message sequence
        // when a sender-key was just shared (see chain definition above).
        incomingMessageChain = incomingMessageChain
          .then(() => get().decryptAndAddMessage(msg))
          .catch((err) => {
            console.error("[WS] message decrypt failed:", err);
          });
      }),
    );

    unsubs.push(
      ws.on("message_edit", async (data) => {
        const msg = (data.message ?? data) as Record<string, unknown>;
        const messageId = (data.messageId ?? msg.id) as string;
        const channelId = data.channelId as string;
        const ciphertext = msg.ciphertext as string | undefined;
        const editedAt = (msg.editedAt ?? new Date().toISOString()) as string;

        // Try to decrypt edited content
        let plaintext: string | undefined;

        // Check ownMessageCache first (covers re-encrypted messages after session reset)
        const cached = ownMessageCache.get(messageId);
        if (cached) {
          plaintext = cached;
        } else {
          const crypto = useCrypto.getState();
          // Detect E2EE by trying to parse as JSON envelope
          let isE2EE = false;
          if (ciphertext) {
            try {
              const parsed = JSON.parse(ciphertext);
              if (parsed.v === 1) isE2EE = true;
            } catch { /* not JSON, not E2EE */ }
          }

          if (crypto.initialized && isE2EE && ciphertext) {
            try {
              const envelope = JSON.parse(ciphertext);
              if (envelope.type === "group_message") {
                plaintext = await crypto.decryptGroupMessage(msg.senderId as string, channelId, ciphertext) ?? undefined;
              } else if (envelope.type === "bot_channel_sender_key_msg") {
                // F16d: bot-channel sender-key message — HKDF + AES-GCM decrypt.
                // Validate envelope shape before decrypt path; pass messageId so the
                // plaintext cache is shared with the original message (Finding 2+3).
                if (!isValidBotChannelSenderKeyEnvelope(envelope)) {
                  e2eeLog.warn("bot_channel_sender_key_msg edit envelope missing required fields", envelope);
                  plaintext = undefined;
                } else {
                  plaintext = await crypto.decryptBotChannelSenderKeyMessage(
                    channelId,
                    msg.senderId as string,
                    envelope,
                    messageId,
                  ) ?? undefined;
                }
              } else {
                plaintext = await crypto.decryptMessage(msg.senderId as string, ciphertext) ?? undefined;
              }
            } catch {
              plaintext = undefined;
            }
          } else {
            plaintext = ciphertext;
          }
        }

        get().updateMessage(
          messageId,
          channelId,
          { ciphertext, plaintext, editedAt },
        );
      }),
    );

    unsubs.push(
      ws.on("message_delete", (data) => {
        const d = data as unknown as { messageId: string; channelId: string };
        get().removeMessage(d.messageId, d.channelId);
      }),
    );

    unsubs.push(
      ws.on("presence", (data) => {
        const d = data as unknown as { userId: string; status: string; lastSeen?: string };
        get().setUserOnline(d.userId, d.status === "online", d.lastSeen);
      }),
    );

    unsubs.push(
      ws.on("typing", (data) => {
        const d = data as unknown as {
          channelId: string;
          userId: string;
          displayName: string;
          isTyping: boolean;
        };
        get().setTyping(d.channelId, d.userId, d.displayName, d.isTyping);
      }),
    );

    unsubs.push(
      ws.on("reaction", (data) => {
        const d = data as unknown as {
          channelId: string;
          messageId: string;
          userId: string;
          emoji: string;
          added: boolean;
        };
        get().updateMessageReaction(d.channelId, d.messageId, d.userId, d.emoji, d.added);
      }),
    );

    unsubs.push(
      ws.on("read_receipt", (data) => {
        const d = data as unknown as { channelId: string; userId: string; messageId: string };
        set((state) => {
          const updated = new Map(state.readReceipts);
          const channelReceipts = new Map(updated.get(d.channelId) ?? []);
          channelReceipts.set(d.userId, d.messageId);
          updated.set(d.channelId, channelReceipts);
          return { readReceipts: updated };
        });
      }),
    );

    unsubs.push(
      ws.on("channel_update", async (data) => {
        const d = data as unknown as {
          channelId: string;
          update: {
            action: string;
            userId: string;
            members: Array<{ userId: string; displayName: string; role: string }>;
            // Fix 4: present when a dm→group conversion happened on member-add.
            channelType?: string;
            channelName?: string | null;
          };
        };

        // channel_deleted: the channel is gone server-side. Remove it from the
        // store instead of falling through to the member-list patch below — that
        // patch writes `members: d.update.members` (undefined for this event),
        // which then crashes any component doing `channel.members.find(...)`
        // (ChannelInfo, Sidebar) and trips the ErrorBoundary ("Etwas ist
        // schiefgelaufen"). The broadcast arrives BEFORE the channel row is
        // deleted, so we must not leave a half-patched channel object behind.
        if (d.update.action === "channel_deleted") {
          set((state) => applyChannelDeleted(state, d.channelId));
          return;
        }

        // Update channel member list. Fix 4: also patch type/name when the
        // server signals a dm→group conversion, so the member_added block below
        // (and the next send) take the group sender-key path instead of the
        // strict pairwise DM path that throws RecipientNotReadyError.
        set((state) => ({
          channels: state.channels.map((ch) =>
            ch.id === d.channelId
              ? {
                  ...ch,
                  members: d.update.members,
                  ...(d.update.channelType ? { type: d.update.channelType as "dm" | "group" } : {}),
                  ...(d.update.channelName !== undefined ? { name: d.update.channelName } : {}),
                }
              : ch,
          ),
        }));

        const crypto = useCrypto.getState();

        // On member_added: exchange sender keys with the new member
        if (d.update.action === "member_added" && crypto.initialized) {
          const channel = get().channels.find((ch) => ch.id === d.channelId);
          if (channel?.type === "group") {
            const { useAuth } = await import("./useAuth");
            const currentUserId = useAuth.getState().user?.id;
            const newUserId = d.update.userId;

            // Backoff schedule for sender-key distribution retries. The new
            // member's device may not be registered yet (just joined / first
            // login still racing), so a single attempt can miss them and the
            // WS event won't fire again. Total wait ~21s — within tolerable
            // group-join UX while covering most onboarding races.
            const SENDER_KEY_RETRY_DELAYS_MS = [1000, 4000, 16000];

            if (newUserId && newUserId !== currentUserId) {
              // Someone else was added → distribute our existing sender key to them.
              // distributeSenderKey is wrapped in withBusy internally, so reload
              // protection is inherited. We re-load senderKey on each attempt in
              // case it was rotated mid-retry (member_removed elsewhere).
              void (async () => {
                const { distributeSenderKey } = await import("./useCrypto-group");
                const { loadSenderKey, saveSenderKey } = await import("../lib/crypto-storage");
                for (let attempt = 0; attempt <= SENDER_KEY_RETRY_DELAYS_MS.length; attempt++) {
                  const senderKey = await loadSenderKey(d.channelId);
                  if (!senderKey) return; // no key yet — encryptForGroup creates + distributes on first send
                  try {
                    const reached = await distributeSenderKey(d.channelId, [newUserId], senderKey, () => useCrypto.getState());
                    if (reached.some((slot) => typeof slot === "string" && slot.startsWith(`${newUserId}:`))) {
                      senderKey.distributedTo = [...new Set([...(senderKey.distributedTo ?? []), ...reached])];
                      await saveSenderKey(senderKey);
                      e2eeLog.info(`Distributed sender key to new member ${newUserId} (attempt ${attempt + 1})`);
                      // F23: bootstrap channel_recovery_key to the new member alongside sender-key.
                      // Required so the new member can decrypt the channel_shared plaintext backup
                      // when live-decrypt fails. Fire-and-forget — recovery-key durability is
                      // best-effort; the sender-key path above is the primary correctness signal.
                      try {
                        const { distributeChannelRecoveryKey } = await import("./useCrypto-group");
                        const { loadChannelRecoveryKey } = await import("../lib/crypto-storage");
                        const recoveryKey = await loadChannelRecoveryKey(d.channelId);
                        if (recoveryKey) {
                          void distributeChannelRecoveryKey(d.channelId, [newUserId], recoveryKey, () => useCrypto.getState());
                        }
                      } catch (err) {
                        e2eeLog.warn(`Recovery-key distribute on member_added for ${newUserId} failed:`, err);
                      }
                      return;
                    }
                  } catch (err) {
                    e2eeLog.warn(`Sender-key distribute attempt ${attempt + 1} for ${newUserId} failed:`, err);
                  }
                  const delay = SENDER_KEY_RETRY_DELAYS_MS[attempt];
                  if (delay !== undefined) {
                    await new Promise((r) => setTimeout(r, delay));
                  } else {
                    e2eeLog.error(`Sender-key distribution to new member ${newUserId} failed after ${SENDER_KEY_RETRY_DELAYS_MS.length + 1} attempts`);
                  }
                }
              })();
            } else if (newUserId && newUserId === currentUserId) {
              // WE were added → eagerly create our sender key and distribute
              // to all existing members. Retry only the members we couldn't
              // reach on the previous attempt; once a member has at least one
              // device-slot in distributedTo we don't need to re-deliver.
              void (async () => {
                const { distributeSenderKey } = await import("./useCrypto-group");
                const { loadSenderKey, saveSenderKey } = await import("../lib/crypto-storage");
                const { toBase64 } = await import("@kollegeplus/crypto");
                let senderKey = await loadSenderKey(d.channelId);
                if (!senderKey) {
                  const keyBytes = window.crypto.getRandomValues(new Uint8Array(32));
                  senderKey = {
                    channelId: d.channelId,
                    senderKey: toBase64(keyBytes),
                    keyId: 1,
                    iteration: 0,
                    createdAt: new Date().toISOString(),
                    distributedTo: [],
                  };
                  await saveSenderKey(senderKey);
                }
                const allOtherMembers = (d.update.members ?? [])
                  .map((m) => m.userId)
                  .filter((id) => id !== currentUserId);
                if (allOtherMembers.length === 0) return;

                let pending = allOtherMembers;
                for (let attempt = 0; attempt <= SENDER_KEY_RETRY_DELAYS_MS.length; attempt++) {
                  try {
                    const reached = await distributeSenderKey(d.channelId, pending, senderKey, () => useCrypto.getState());
                    senderKey.distributedTo = [...new Set([...(senderKey.distributedTo ?? []), ...reached])];
                    await saveSenderKey(senderKey);
                    pending = pending.filter(
                      (uid) => !senderKey!.distributedTo!.some((slot) => typeof slot === "string" && slot.startsWith(`${uid}:`)),
                    );
                    if (pending.length === 0) {
                      e2eeLog.info(`Created & distributed sender key to ${allOtherMembers.length} existing members (attempt ${attempt + 1})`);
                      return;
                    }
                  } catch (err) {
                    e2eeLog.warn(`Sender-key initial distribute attempt ${attempt + 1} failed:`, err);
                  }
                  const delay = SENDER_KEY_RETRY_DELAYS_MS[attempt];
                  if (delay !== undefined) {
                    await new Promise((r) => setTimeout(r, delay));
                  } else {
                    e2eeLog.error(`Sender-key distribution incomplete after ${SENDER_KEY_RETRY_DELAYS_MS.length + 1} attempts — ${pending.length} member(s) still missing`);
                  }
                }
              })();
            }
          }
        }

        // On member_removed: rotate sender keys for the group
        if (d.update.action === "member_removed" && crypto.initialized) {
          const channel = get().channels.find((ch) => ch.id === d.channelId);
          if (channel?.type === "group") {
            const { useAuth } = await import("./useAuth");
            const currentUserId = useAuth.getState().user?.id;
            const remainingMemberIds = d.update.members
              .filter((m) => m.userId !== currentUserId)
              .map((m) => m.userId);
            crypto.rotateSenderKey(d.channelId, remainingMemberIds).catch((err) => {
              e2eeLog.warn("Failed to rotate sender key:", err);
            });
          }
        }
      }),
    );

    // ── Channel Added (new DM/group from another user) ──
    unsubs.push(
      ws.on("channel_added", (data) => {
        const d = data as unknown as { channel: Channel };
        if (!d.channel) return;

        set((state) => {
          // Avoid duplicates
          if (state.channels.some((ch) => ch.id === d.channel.id)) return state;
          return { channels: [d.channel, ...state.channels] };
        });

        // Subscribe to the new channel on WS
        ws.send({ type: "subscribe", channelIds: [d.channel.id] });

        // KOL-103: fetch presence for the new channel's members — without this
        // they render offline until the next full reload.
        const presenceIds = d.channel.members?.map((m) => m.userId) ?? [];
        if (presenceIds.length > 0) void get().loadPresence(presenceIds);
      }),
    );

    // ── Bot Streaming Events ──
    // (Timer maps declared at top of initWebSocketHandlers so ws.on("message") can use them.)

    unsubs.push(
      ws.on("bot_activity", (data) => {
        const d = data as unknown as {
          channelId: string;
          botUserId: string;
          text?: string;
          encrypted?: boolean;
          envelope?: string;
        };
        const apply = (text: string) => {
          set((state) => {
            const updated = new Map(state.botActivity);
            updated.set(d.channelId, { botUserId: d.botUserId, text, ts: Date.now() });
            return { botActivity: updated };
          });
          const existing = botActivityTimers.get(d.channelId);
          if (existing) clearTimeout(existing);
          botActivityTimers.set(d.channelId, setTimeout(() => {
            botActivityTimers.delete(d.channelId);
            set((state) => {
              const updated = new Map(state.botActivity);
              updated.delete(d.channelId);
              return { botActivity: updated };
            });
          }, BOT_ACTIVITY_TTL_MS));
        };
        if (d.encrypted && d.envelope) {
          void (async () => {
            try {
              const envelopeObj = JSON.parse(d.envelope!) as unknown;
              if (!isValidBotChannelSenderKeyEnvelope(envelopeObj)) return;
              const plaintext = await useCrypto.getState().decryptBotChannelSenderKeyMessage(
                d.channelId,
                d.botUserId,
                envelopeObj,
              );
              if (plaintext) apply(plaintext);
            } catch {
              // drop silently — ticker is best-effort
            }
          })();
          return;
        }
        if (d.text) apply(d.text);
      }),
    );

    unsubs.push(
      ws.on("bot_stream_start", (data) => {
        {
          const sd = data as unknown as { channelId?: string };
          if (sd.channelId) {
            const t = botActivityTimers.get(sd.channelId);
            if (t) { clearTimeout(t); botActivityTimers.delete(sd.channelId); }
            set((state) => {
              if (!state.botActivity.has(sd.channelId!)) return state;
              const updated = new Map(state.botActivity);
              updated.delete(sd.channelId!);
              return { botActivity: updated };
            });
          }
        }
        // F16g: encrypted flag + keyId present for group-channel streams.
        // error:"setup_failed" means the server could not distribute the sender key
        // for this group channel — no plaintext chunks will arrive. Show a static
        // "[Diana antwortet…]" placeholder so the user sees activity without any
        // plaintext being exposed.
        const d = data as unknown as { channelId: string; botUserId: string; encrypted?: boolean; keyId?: string | null; error?: string };
        const encryptionSetupFailed = d.encrypted === true && d.error === "setup_failed";
        set((state) => {
          const updated = new Map(state.botStreams);
          updated.set(d.channelId, {
            channelId: d.channelId,
            botUserId: d.botUserId,
            content: "",
            isStreaming: !encryptionSetupFailed,
            ...(encryptionSetupFailed ? { encryptionSetupFailed: true } : {}),
          });
          return { botStreams: updated };
        });

        // Set safety timeout. F16: don't delete the bubble on timeout — convert
        // to "finalizing" so the user keeps seeing activity instead of silent
        // disappearance. Hard-fail timer kicks in later and surfaces an error.
        const existing = botStreamTimeouts.get(d.channelId);
        if (existing) clearTimeout(existing);
        botStreamTimeouts.set(d.channelId, setTimeout(() => {
          botStreamTimeouts.delete(d.channelId);
          set((state) => {
            const updated = new Map(state.botStreams);
            const stream = updated.get(d.channelId);
            if (stream && stream.isStreaming) {
              updated.set(d.channelId, {
                ...stream,
                isStreaming: false,
                finalizing: true,
                endedAt: Date.now(),
              });
              return { botStreams: updated };
            }
            return state;
          });
          // Reload messages to pick up any that arrived while we were stuck
          get().loadMessages(d.channelId);
        }, BOT_STREAM_TIMEOUT_MS));

        // Hard-fail timer: if nothing arrives at all, mark failed so the UI can
        // show a warning + retry hint instead of an indefinite spinner.
        const existingFail = botStreamHardFailTimers.get(d.channelId);
        if (existingFail) clearTimeout(existingFail);
        botStreamHardFailTimers.set(d.channelId, setTimeout(() => {
          botStreamHardFailTimers.delete(d.channelId);
          set((state) => {
            const updated = new Map(state.botStreams);
            const stream = updated.get(d.channelId);
            if (stream) {
              updated.set(d.channelId, { ...stream, isStreaming: false, finalizing: false, failed: true });
              return { botStreams: updated };
            }
            return state;
          });
        }, BOT_STREAM_HARD_FAIL_MS));
      }),
    );

    unsubs.push(
      ws.on("bot_stream_chunk", (data) => {
        const d = data as unknown as {
          channelId: string;
          botUserId: string;
          // Plaintext path (DM / legacy):
          fullContent?: string;
          // F16g encrypted path (group channels):
          encrypted?: boolean;
          envelope?: string;
        };

        // F16g: if the chunk is encrypted, decrypt before updating the stream bubble.
        if (d.encrypted && d.envelope) {
          void (async () => {
            const crypto = useCrypto.getState();
            try {
              const envelopeObj = JSON.parse(d.envelope!) as unknown;
              if (!isValidBotChannelSenderKeyEnvelope(envelopeObj)) {
                // Malformed envelope — drop chunk, do not render plaintext.
                return;
              }
              const plaintext = await crypto.decryptBotChannelSenderKeyMessage(
                d.channelId,
                d.botUserId,
                envelopeObj,
              );
              if (!plaintext) {
                // Cache miss or auth failure — drop chunk.
                // The persisted message's decrypt will retry on stream_end.
                return;
              }
              // Accumulated content: append decrypted delta to existing stream content.
              set((state) => {
                const updated = new Map(state.botStreams);
                const existing = updated.get(d.channelId);
                if (existing) {
                  updated.set(d.channelId, {
                    ...existing,
                    content: (existing.content ?? "") + plaintext,
                    isStreaming: true,
                    finalizing: false,
                    failed: false,
                    endedAt: undefined,
                  });
                }
                return { botStreams: updated };
              });
            } catch {
              // Parse error or decrypt error — drop chunk silently.
            }
          })();
          return;
        }

        // Plaintext / legacy path.
        set((state) => {
          const updated = new Map(state.botStreams);
          const existing = updated.get(d.channelId);
          if (existing) {
            // Any chunk = liveness signal: drop finalizing/failed, mark streaming again.
            updated.set(d.channelId, {
              ...existing,
              content: d.fullContent ?? existing.content,
              isStreaming: true,
              finalizing: false,
              failed: false,
              endedAt: undefined,
            });
          }
          return { botStreams: updated };
        });
      }),
    );

    unsubs.push(
      ws.on("bot_stream_end", (data) => {
        const d = data as unknown as { channelId: string; fullContent?: string; error?: boolean; messageId?: string };
        // Clear safety timeout — we're not waiting for end any more
        const timer = botStreamTimeouts.get(d.channelId);
        if (timer) {
          clearTimeout(timer);
          botStreamTimeouts.delete(d.channelId);
        }
        // Hard-fail timer no longer needed once end arrived (good or error).
        const failTimer = botStreamHardFailTimers.get(d.channelId);
        if (failTimer) {
          clearTimeout(failTimer);
          botStreamHardFailTimers.delete(d.channelId);
        }

        // On error: clear immediately (no message to wait for, server already sent
        // an apology that takes the bubble's place)
        if (d.error) {
          set((state) => {
            const updated = new Map(state.botStreams);
            updated.delete(d.channelId);
            return { botStreams: updated };
          });
          return;
        }

        // Mark stream as ended but keep bubble visible. The cursor disappears, and
        // the bubble stays until the persisted "message" event arrives (handled in
        // the ws.on("message") handler) or BOT_STREAM_END_GRACE_MS elapses.
        set((state) => {
          const updated = new Map(state.botStreams);
          const existing = updated.get(d.channelId);
          if (existing) {
            updated.set(d.channelId, {
              ...existing,
              isStreaming: false,
              content: d.fullContent ?? existing.content,
              endedAt: Date.now(),
            });
          }
          return { botStreams: updated };
        });

        // Cleanup timer (F16): when grace expires without a persisted message,
        // switch to "finalizing" rather than deleting. Bubble keeps its content
        // (faded) plus a spinner — user sees the system is still working.
        // Hard-fail timer (set on start) later flips to "failed" if nothing comes.
        const existingCleanup = botStreamCleanupTimers.get(d.channelId);
        if (existingCleanup) clearTimeout(existingCleanup);
        botStreamCleanupTimers.set(d.channelId, setTimeout(() => {
          botStreamCleanupTimers.delete(d.channelId);
          set((state) => {
            const updated = new Map(state.botStreams);
            const stream = updated.get(d.channelId);
            if (stream && !stream.isStreaming && stream.endedAt && !stream.finalizing) {
              updated.set(d.channelId, { ...stream, finalizing: true });
              return { botStreams: updated };
            }
            return state;
          });
          // Reload to ensure the persisted message renders even if the WS event was lost
          get().loadMessages(d.channelId);
        }, BOT_STREAM_END_GRACE_MS));
      }),
    );

    // F16 Fix 2: bot_typing_start/end — typing indicator for in-flight bot work
    // without (or before) any token stream. Covers reasoning, tool-use, and
    // post-processing gaps where bot_stream_chunk doesn't fire.
    unsubs.push(
      ws.on("bot_typing_start", (data) => {
        const d = data as unknown as { channelId: string; botUserId: string };
        set((state) => {
          const updated = new Map(state.botTyping);
          updated.set(d.channelId, { channelId: d.channelId, botUserId: d.botUserId, since: Date.now() });
          return { botTyping: updated };
        });
        const existing = botTypingTimers.get(d.channelId);
        if (existing) clearTimeout(existing);
        botTypingTimers.set(d.channelId, setTimeout(() => {
          botTypingTimers.delete(d.channelId);
          set((state) => {
            const updated = new Map(state.botTyping);
            updated.delete(d.channelId);
            return { botTyping: updated };
          });
        }, BOT_TYPING_TIMEOUT_MS));
      }),
    );
    unsubs.push(
      ws.on("bot_typing_end", (data) => {
        const d = data as unknown as { channelId: string };
        const t = botTypingTimers.get(d.channelId);
        if (t) {
          clearTimeout(t);
          botTypingTimers.delete(d.channelId);
        }
        set((state) => {
          const updated = new Map(state.botTyping);
          updated.delete(d.channelId);
          return { botTyping: updated };
        });
      }),
    );

    // Handle E2EE session reset request (from bot or peer)
    unsubs.push(
      ws.on("e2ee_session_reset", async (data) => {
        const d = data as unknown as { channelId?: string; botUserId?: string; peerId?: string; failedMessageId?: string; fromDeviceId?: string };
        const targetPeerId = d.peerId ?? d.botUserId;
        e2eeLog.warn("[F18b] cascade_entry", {
          channelId: d.channelId,
          targetPeerId,
          failedMessageId: d.failedMessageId,
          hasFailedMessageId: !!d.failedMessageId,
          fromDeviceId: d.fromDeviceId,
        });
        e2eeLog.warn("Session reset requested", { peerId: targetPeerId, failedMessageId: d.failedMessageId, fromDeviceId: d.fromDeviceId });
        const crypto = useCrypto.getState();
        if (crypto.initialized && targetPeerId) {
          // User-to-user reset (no failedMessageId): peer couldn't decrypt our messages.
          // Delete our session so next message triggers fresh X3DH.
          if (!d.failedMessageId) {
            e2eeLog.warn("Peer-initiated session reset — deleting session for", targetPeerId);
            // Wrap the whole delete-then-redistribute flow in withBusy so auto-reload
            // cannot fire between the two awaits and leave sender keys un-distributed.
            await withBusy(async () => {
              e2eeLog.warn("[F18b] cascade_delete_session", { peerId: targetPeerId });
              await crypto.deleteSessionForPeer(targetPeerId);

              // Re-distribute sender keys for all shared group channels after session reset.
              // The stale session was just deleted, so distributeSenderKey will create a fresh
              // X3DH session and encrypt the sender key as an initial message.
              const { useAuth } = await import("./useAuth");
              const currentUserId = useAuth.getState().user?.id;
              if (currentUserId) {
                const sharedGroups = get().channels.filter(
                  (ch) => ch.type === "group" && ch.members?.some((m) => m.userId === targetPeerId),
                );
                if (sharedGroups.length > 0) {
                  const { distributeSenderKey } = await import("./useCrypto-group");
                  const { loadSenderKey } = await import("../lib/crypto-storage");
                  for (const group of sharedGroups) {
                    const senderKey = await loadSenderKey(group.id);
                    if (senderKey) {
                      await distributeSenderKey(group.id, [targetPeerId], senderKey, () => useCrypto.getState()).catch((err) => {
                        e2eeLog.warn("Failed to re-distribute sender key after session reset:", err);
                      });
                      e2eeLog.info("Re-distributed sender key to", targetPeerId, "for channel", group.id, "after session reset");
                    }
                  }
                }
              }
            });

            return;
          }

          // Bot-initiated reset with failedMessageId: run cascade to recover plaintext.

          // (C) Loop guard: keyed on (failedMessageId, targetPeerId) so multiple
          // registry bots each get an independent attempt instead of the first
          // bot suppressing all subsequent ones.
          const loopGuardKey = `${d.failedMessageId}:${targetPeerId}`;
          if (recentlyAutoRetriedResets.has(loopGuardKey)) {
            e2eeLog.warn("Suppressing repeated auto-rewrap for failedMessageId (already retried in last 60s)", { messageId: d.failedMessageId, targetPeerId });
            return;
          }
          recentlyAutoRetriedResets.add(loopGuardKey);
          setTimeout(() => recentlyAutoRetriedResets.delete(`${d.failedMessageId!}:${targetPeerId}`), 60_000);

          // T3 cross-channel guard: verify the failed message actually lives in the
          // claimed channelId — prevents a rogue bot from triggering a re-encrypt
          // of a message from a different channel.
          const origMsgs = get().messages.get(d.channelId!);
          const origMsg = origMsgs?.find((m) => m.id === d.failedMessageId);
          if (!origMsg) {
            e2eeLog.warn("Ignoring session reset: failedMessageId not found in channelId", { messageId: d.failedMessageId, channelId: d.channelId });
            // Still delete the session so we don't leave stale state
            await withBusy(async () => {
              e2eeLog.warn("[F18b] cascade_delete_session", { peerId: targetPeerId });
              await crypto.deleteSessionForPeer(targetPeerId);
            });
            return;
          }

          // T5 peer-membership guard: verify targetPeerId is actually a member of
          // the channel — closes the bot-impersonation / cross-peer forge route.
          const channel = get().channels.find((c) => c.id === d.channelId);
          if (!channel?.members?.some((m) => m.userId === targetPeerId)) {
            e2eeLog.warn("Ignoring session reset: targetPeerId not channel member", { messageId: d.failedMessageId, channelId: d.channelId, targetPeerId });
            await withBusy(async () => {
              e2eeLog.warn("[F18b] cascade_delete_session", { peerId: targetPeerId });
              await crypto.deleteSessionForPeer(targetPeerId);
            });
            return;
          }

          // Delete session + run cascade + re-encrypt as one atomic unit so
          // auto-reload cannot fire between the delete and the re-encrypt.
          await withBusy(async () => {
            // ALWAYS delete the session first — clean state for the next outgoing
            // message regardless of whether we manage to recover plaintext below.
            e2eeLog.warn("[F18b] cascade_delete_session", { peerId: targetPeerId });
            await crypto.deleteSessionForPeer(targetPeerId);

            // 3-step cascade bounded at 5 s total:
            //   1. in-memory ownMessageCache (fastest, no I/O)
            //   2. IDB plaintextCache (persistent across reloads)
            //   3. server-side backup decrypt
            const cascade = async (): Promise<string | null> => {
              const inMem = ownMessageCache.get(d.failedMessageId!);
              if (inMem && isRealPlaintext(inMem)) return inMem;
              const idb = await getCachedPlaintext(d.failedMessageId!);
              if (idb && isRealPlaintext(idb)) {
                ownMessageCache.set(d.failedMessageId!, idb); // promote to in-memory
                return idb;
              }
              const backup = await tryBackupDecrypt({ id: d.failedMessageId!, channelId: d.channelId! });
              if (typeof backup === "string") {
                ownMessageCache.set(d.failedMessageId!, backup);
                return backup;
              }
              return null;
            };
            const plaintext = await Promise.race([
              cascade(),
              new Promise<null>((resolve) => setTimeout(() => resolve(null), 5000)),
            ]);

            if (plaintext) {
              if (channel?.type === "group") {
                // GROUP recovery: NEVER rewrite a group message into a pairwise
                // edit. encryptForPeer produces a single-peer `type:"message"`
                // envelope; an edit_message then overwrites the canonical
                // messages.ciphertext with it, so EVERY other group member who
                // hadn't decrypted yet now sees a pairwise envelope they can't
                // read → cascading sender-key-miss across the whole channel (and
                // the registry-bot fallback gets a pairwise blob). The correct
                // group recovery is to (re)distribute our sender key to the
                // failing peer so it can decrypt the ORIGINAL group_message; the
                // message content is never touched. Mirrors the no-failedMessageId
                // branch above.
                e2eeLog.warn("[F18b] cascade_group_redistribute (no pairwise edit)", { messageId: d.failedMessageId, channelId: d.channelId, targetPeerId });
                try {
                  const { distributeSenderKey } = await import("./useCrypto-group");
                  const { loadSenderKey } = await import("../lib/crypto-storage");
                  const senderKey = await loadSenderKey(d.channelId!);
                  if (senderKey) {
                    await distributeSenderKey(d.channelId!, [targetPeerId!], senderKey, () => useCrypto.getState());
                    e2eeLog.info("Re-distributed sender key to", targetPeerId, "for group", d.channelId, "after decrypt-fail");
                  } else {
                    e2eeLog.warn("No local sender key for group — cannot redistribute", { channelId: d.channelId });
                  }
                } catch (err) {
                  e2eeLog.error("Failed to re-distribute sender key after group decrypt-fail", err);
                }
              } else {
                // DM recovery: pairwise re-encrypt + edit is safe — the single
                // peer owns this message's ciphertext.
                e2eeLog.warn("Re-encrypting failed message and sending as edit", { messageId: d.failedMessageId });
                try {
                  const newCiphertext = await crypto.encryptForPeer(targetPeerId!, plaintext);
                  if (newCiphertext && d.channelId) {
                    ws.send({
                      type: "edit_message",
                      messageId: d.failedMessageId,
                      channelId: d.channelId,
                      ciphertext: newCiphertext,
                    });
                    e2eeLog.warn("[F18b] cascade_edit_sent", {
                      messageId: d.failedMessageId,
                      channelId: d.channelId,
                      ciphertextLen: newCiphertext?.length ?? 0,
                    });
                  }
                } catch (err) {
                  e2eeLog.error("Failed to re-encrypt message after session reset", err);
                }
              }
            } else {
              e2eeLog.warn("No plaintext recoverable — session deleted, message stays unsent", { messageId: d.failedMessageId });
            }
          });
        }
      }),
    );

    // Handle device_deactivated: server has deactivated this device (e.g. cascade).
    // Attempt to self-heal by re-registering the key bundle once per page session.
    // On a second deactivation event, stop healing and prompt the user instead.
    unsubs.push(
      ws.on("device_deactivated", (data) => {
        const d = data as unknown as { reason?: string };
        const reRegister = async (): Promise<void> => {
          // F22c: Route through the IDB-truth guard (_registerDeviceWithPhantomRetry)
          // instead of calling api.post directly. The guard re-reads IndexedDB at
          // call time and substitutes any fresher keys, preventing stale tab-memory
          // from being published (the H1 incident path, 2026-06-07 08:39).
          const cryptoState = useCrypto.getState();
          if (!cryptoState.deviceId || !cryptoState.deviceKeys) {
            throw new Error("device-heal: crypto not initialized");
          }
          const { exportPublicKeys } = await import("@kollegeplus/crypto");
          const { getFirstDeviceKeys } = await import("../lib/crypto-storage");
          const { serializeDeviceKeys } = await import("./useCrypto-helpers");

          // Read the stored (serialized) keys so _registerDeviceWithPhantomRetry
          // can re-serialize them when the IDB row substitutes stale memory.
          const idbStored = await getFirstDeviceKeys();
          const storedDeviceKeys =
            idbStored?.deviceId === cryptoState.deviceId
              ? idbStored
              : serializeDeviceKeys(cryptoState.deviceId, cryptoState.deviceKeys);
          const publicKeys = exportPublicKeys(cryptoState.deviceKeys);

          try {
            await _registerDeviceWithPhantomRetry(
              cryptoState.deviceId,
              publicKeys,
              storedDeviceKeys,
              useCrypto.getState,
              (partial) => useCrypto.setState(partial),
            );
          } catch (regErr) {
            if (regErr instanceof Error && regErr.message.startsWith("idb_device_mismatch")) {
              // Another tab already rotated the device identity via phantom-retry.
              // Publishing the stale deviceId here would resurrect a zombie — treat
              // as a non-retryable failure so handleDeviceDeactivated surfaces the
              // clear-data prompt without crashing.
              e2eeLog.warn("[device-heal] idb_device_mismatch — another tab rotated device; skipping re-register", regErr.message);
              throw regErr;
            }
            // reactivation_blocked and all other errors propagate unchanged so
            // handleDeviceDeactivated's generic catch emits the failure signal.
            throw regErr;
          }
          // Clear the cached registration fingerprint so a fresh initialize() (e.g. after reload) won't skip re-registration.
          localStorage.removeItem("kp_keys_fingerprint");
          e2eeLog.info("[device-heal] Re-registration after device_deactivated succeeded");
        };
        void handleDeviceDeactivated(
          { reason: d.reason ?? "unknown" },
          {
            deviceId: useCrypto.getState().deviceId ?? "unknown",
            reRegister,
            toast: (msg: string) => {
              e2eeLog.warn("[device-heal]", msg);
              window.alert(msg);
            },
            emit: (ev) => {
              void import("../lib/e2ee-telemetry").then(({ reportE2eeError }) =>
                reportE2eeError({
                  phase: "reregister_attempt",
                  deviceId: ev.deviceId,
                  error: `source=${ev.source} success=${ev.success}`,
                }),
              );
            },
            // CR1 cascade auto-recovery: wipe the corrupt ratchet state + re-init
            // onto a fresh deviceId instead of the dead-end clear-data alert.
            clearCorruptState: async () => {
              const { clearCorruptRatchetState } = await import("../lib/crypto-storage");
              await clearCorruptRatchetState();
            },
            reinitCrypto: async () => {
              // CR1 fix: drop the persisted device identity FIRST. generateDeviceId()
              // reads kollegeplus:deviceId / kp_deviceId cookie before minting, so
              // without this the re-init resurrects the cascade-dead deviceId (old id
              // + fresh keys) and the bot's stale session for it keeps failing. Clearing
              // it forces a genuinely fresh id → clean peer/bot sessions.
              const { clearPersistedDeviceId } = await import("./useCrypto-helpers");
              clearPersistedDeviceId();
              useCrypto.setState({
                initialized: false, isInitializing: false,
                deviceId: null, deviceKeys: null, sessions: new Map(),
              });
              await useCrypto.getState().initialize();
            },
            // Zombie-tab stopper: when cascade auto-recovery is rate-limited/failed,
            // drop the persisted deviceId and hard-reload so the next load mints a
            // fresh device instead of leaving this dead tab spamming decrypt failures.
            forceReload: () => {
              void import("./useCrypto-helpers").then(({ clearPersistedDeviceId }) => {
                clearPersistedDeviceId();
                localStorage.removeItem("kp_keys_fingerprint");
                window.location.reload();
              });
            },
          },
        );
      }),
    );

    // Handle sender key request: another member needs our sender key
    unsubs.push(
      ws.on("request_sender_key", async (data) => {
        const d = data as unknown as { channelId?: string; requesterId?: string };
        if (!d.channelId || !d.requesterId) return;
        const crypto = useCrypto.getState();
        if (!crypto.initialized) return;
        const { handleSenderKeyRequest } = await import("./useCrypto-group");
        await handleSenderKeyRequest(d.channelId, d.requesterId, () => useCrypto.getState());
      }),
    );

    // Handle proxy sender key request: server asks us to redistribute an
    // offline user's sender key to a requester (we may have it with seed)
    unsubs.push(
      ws.on("proxy_sender_key_request", async (data) => {
        const d = data as unknown as { channelId?: string; senderId?: string; requesterId?: string };
        if (!d.channelId || !d.senderId || !d.requesterId) return;
        const crypto = useCrypto.getState();
        if (!crypto.initialized) return;
        const { handleProxySenderKeyRequest } = await import("./useCrypto-group");
        await handleProxySenderKeyRequest(d.channelId, d.senderId, d.requesterId, () => useCrypto.getState());
      }),
    );

    // F23b: Handle proxy_redistribute_request — server asks us to redistribute
    // our OWN sender key + recovery key to a requester who has none
    // (e.g. they re-logged in after all their devices were inactive).
    // Reuses handleSenderKeyRequest (which also distributes peer proxy keys)
    // plus distributeChannelRecoveryKey for the recovery path.
    unsubs.push(
      ws.on("proxy_redistribute_request", async (data) => {
        const d = data as unknown as { channelId?: string; requesterId?: string };
        if (!d.channelId || !d.requesterId) return;
        const crypto = useCrypto.getState();
        if (!crypto.initialized) return;

        const { reportE2eeError } = await import("../lib/e2ee-telemetry");
        const { loadSenderKey, loadChannelRecoveryKey } = await import("../lib/crypto-storage");

        e2eeLog.log("[chat] F23b: ← proxy_redistribute_request", { channelId: d.channelId, requesterId: d.requesterId });

        // 1. Distribute our own sender key (+ proxy keys for our other devices)
        const { handleSenderKeyRequest } = await import("./useCrypto-group");
        const senderKey = await loadSenderKey(d.channelId);
        if (senderKey) {
          await handleSenderKeyRequest(d.channelId, d.requesterId, () => useCrypto.getState());
        }

        // 2. Distribute the channel recovery key if we have it
        const recoveryKey = await loadChannelRecoveryKey(d.channelId);
        if (recoveryKey) {
          const { distributeChannelRecoveryKey } = await import("./useCrypto-group");
          void distributeChannelRecoveryKey(d.channelId, [d.requesterId], recoveryKey, () => useCrypto.getState());
        }

        reportE2eeError({
          phase: "redistribute_request_received",
          error: `channelId=${d.channelId} requesterId=${d.requesterId} hasSk=${!!senderKey} hasRk=${!!recoveryKey}`,
        });
      }),
    );

    // F22c: Handle proxy_recovery_key_request — server asks us to re-distribute
    // the channelRecoveryKey to a member that lacks it. If we hold the key locally,
    // call distributeChannelRecoveryKey targeting only the requester's userId so
    // X3DH envelopes land in their pending_sender_keys rows.
    unsubs.push(
      ws.on("proxy_recovery_key_request", async (data) => {
        const d = data as unknown as { channelId?: string; requesterUserId?: string; requesterDeviceId?: string };
        if (!d.channelId || !d.requesterUserId || !d.requesterDeviceId) return;
        const crypto = useCrypto.getState();
        if (!crypto.initialized) return;

        const { reportE2eeError } = await import("../lib/e2ee-telemetry");
        reportE2eeError({
          phase: "recovery_key_request_proxy_received",
          deviceId: crypto.deviceId,
          error: `channelId=${d.channelId} requesterUserId=${d.requesterUserId}`,
        });

        const { loadChannelRecoveryKey } = await import("../lib/crypto-storage");
        const recoveryKey = await loadChannelRecoveryKey(d.channelId);
        if (!recoveryKey) {
          // We don't have the key either — nothing to offer
          e2eeLog.info("F22c: proxy_recovery_key_request for", d.channelId, "— no local key, skipping");
          reportE2eeError({
            phase: "recovery_key_no_holder",
            deviceId: crypto.deviceId,
            error: `channelId=${d.channelId} requesterUserId=${d.requesterUserId}`,
          });
          return;
        }

        e2eeLog.info("F22c: proxy_recovery_key_request — redistributing to", d.requesterUserId, "channel", d.channelId);
        reportE2eeError({
          phase: "recovery_key_redistributed",
          deviceId: crypto.deviceId,
          error: `channelId=${d.channelId} requesterUserId=${d.requesterUserId}`,
        });
        const { distributeChannelRecoveryKey } = await import("./useCrypto-group");
        void distributeChannelRecoveryKey(
          d.channelId,
          [d.requesterUserId],
          recoveryKey,
          () => useCrypto.getState(),
        ).then((reached) => {
          e2eeLog.info("F22c: recovery key redistributed to", reached.length, "device slot(s) for", d.requesterUserId);
        }).catch((err) => {
          e2eeLog.warn("F22c: recovery-key redistribute to", d.requesterUserId, "failed:", err);
        });
      }),
    );

    // P5: Identity-Change → Group-Sender-Key-Reset
    //
    // The server sends `sender_key_identity_reset` to our own WS connection when
    // this user's identity key rotated (device re-registered with a new identity).
    // The old sender key was distributed via X3DH envelopes bound to the OLD
    // identity — receivers who cached that key will fail to validate messages
    // from the new identity. We must:
    //   1. Delete the stale local sender key for the given channel.
    //   2. Generate a fresh sender key (new keyId) and distribute to all current members.
    //
    // This closes the root cause of C6 (84% of sender-key-miss plaintext-failsafe reads).
    //
    // Rate-guard: per-channel per-session dedup so a reconnect storm after
    // an identity change doesn't trigger O(N_channels) rotations in parallel.
    // The server already caps delivery to WAKE_REDISTRIBUTE_MAX_CHANNELS = 50.
    const identityResetHandled = new Set<string>(); // keyed by channelId

    unsubs.push(
      ws.on("sender_key_identity_reset", async (data) => {
        const d = data as unknown as { channelId?: string; memberIds?: string[] };
        if (!d.channelId || !Array.isArray(d.memberIds)) return;

        // Dedupe within session — drain may deliver the same channel twice on a
        // race between direct WS push and drain; rotation is idempotent but
        // doing it twice wastes two rounds of X3DH distribute calls.
        if (identityResetHandled.has(d.channelId)) return;
        identityResetHandled.add(d.channelId);

        const crypto = useCrypto.getState();
        if (!crypto.initialized) return;

        const { useAuth } = await import("./useAuth");
        const currentUserId = useAuth.getState().user?.id;

        // Filter out self from memberIds — rotateSenderKey's distributeSenderKey
        // re-adds currentUserId internally, but cleaner to keep the list tidy.
        const memberIds = d.memberIds.filter((id) => id !== currentUserId);

        e2eeLog.log(
          "[chat] P5: ← sender_key_identity_reset",
          { channelId: d.channelId, memberCount: memberIds.length },
        );

        // P2: An identity reset means all previously-cached sender keys for
        // this channel are stale. Clear failsafe miss markers NOW (before the
        // rotation) so any messages that hit the failsafe under the old identity
        // can re-try decryption once the new key is distributed.  This fires
        // even if rotateSenderKey later throws, which is intentional — a failed
        // rotation should still unblock the retry path.
        clearPeerPlaintextFailsafeMarkers(d.channelId);

        try {
          const { reportE2eeError } = await import("../lib/e2ee-telemetry");
          reportE2eeError({
            phase: "identity_reset_rotate",
            deviceId: crypto.deviceId,
            error: `channelId=${d.channelId} memberCount=${memberIds.length}`,
          });
          // Rotate: generates fresh AES sender key with incremented keyId and
          // distributes X3DH envelopes under the NEW identity to all members.
          await crypto.rotateSenderKey(d.channelId, memberIds);
          e2eeLog.log("[chat] P5: sender key rotated + redistributed for channel", d.channelId);
          // Notify any parked decrypts to retry — members who receive the new key
          // via pending_sender_keys poll will drain and re-decrypt.
          window.dispatchEvent(new CustomEvent("sender-key-received", { detail: { channelId: d.channelId } }));
        } catch (err) {
          e2eeLog.warn("[chat] P5: sender_key_identity_reset rotate failed for channel", d.channelId, err);
        }
      }),
    );

    // ── Bug D: peer device asks us to forward user_backup_key via X3DH ──────
    // We are a peer device that (hopefully) has the user_backup_key cached.
    // Build a sender_key_x3dh envelope with inner payload type "user_backup_key"
    // and POST to /users/keys/distribute (same user, different device).
    unsubs.push(
      ws.on("proxy_backup_key_request", async (data) => {
        const d = data as unknown as { requesterDeviceId?: string };
        if (!d.requesterDeviceId) return;
        const crypto = useCrypto.getState();
        if (!crypto.initialized || !crypto.deviceId || !crypto.deviceKeys) return;

        try {
          const { reportE2eeError } = await import("../lib/e2ee-telemetry");
          reportE2eeError({ phase: "backup_key_request_received_proxy", deviceId: crypto.deviceId });
          const { getRawBackupKeyBase64 } = await import("../lib/backup-key");
          const rawB64 = getRawBackupKeyBase64();
          if (!rawB64) {
            // We also don't have the backup key — report and try requesting from our own peers
            e2eeLog.info("[Bug D] proxy_backup_key_request: we have no backup key either, skipping");
            reportE2eeError({ phase: "backup_key_no_peer_has_it", deviceId: crypto.deviceId });
            import("./useCrypto").then(({ requestUserBackupKey }) => { requestUserBackupKey(); }).catch(() => {});
            return;
          }

          const { useAuth } = await import("./useAuth");
          const currentUserId = useAuth.getState().user?.id;
          if (!currentUserId) return;

          // Fetch the requester device's pre-key bundle
          const peerKeysResponse = await api.post<{ keys: Record<string, Array<{ deviceId: string; identityKey: string; signingKey: string; signedPreKey: { keyId: number; publicKey: string; signature: string }; preKey?: { keyId: number; publicKey: string } }>> }>(
            "/users/keys",
            { userIds: [currentUserId] },
          );
          const myDevices = peerKeysResponse.keys[currentUserId] ?? [];
          const targetBundle = myDevices.find((d2) => d2.deviceId === d.requesterDeviceId);
          if (!targetBundle) {
            e2eeLog.warn("[Bug D] proxy_backup_key_request: requester device not found in /users/keys");
            return;
          }

          const { createSession, fromBase64 } = await import("@kollegeplus/crypto");
          const bundle = {
            identityKey: fromBase64(targetBundle.identityKey),
            signingKey: fromBase64(targetBundle.signingKey),
            signedPreKey: {
              keyId: targetBundle.signedPreKey.keyId,
              publicKey: fromBase64(targetBundle.signedPreKey.publicKey),
              signature: fromBase64(targetBundle.signedPreKey.signature),
            },
            preKey: targetBundle.preKey
              ? { keyId: targetBundle.preKey.keyId, publicKey: fromBase64(targetBundle.preKey.publicKey) }
              : null,
          };

          const innerPayload = JSON.stringify({ v: 1, type: "user_backup_key", payload: { key: rawB64 } });
          const tempSession = createSession(crypto.deviceKeys.identityKeyPair, bundle);
          const encrypted = tempSession.encrypt(innerPayload);

          const envelope = JSON.stringify({
            v: 1,
            type: "sender_key_x3dh",
            senderDeviceId: crypto.deviceId,
            payload: encrypted,
          });

          await api.post("/users/keys/distribute", {
            recipientId: currentUserId,          // same user, other device
            recipientDeviceId: d.requesterDeviceId,
            envelope,
            // No channelId — user_backup_key is user-scoped, not channel-scoped
          });

          e2eeLog.info("[Bug D] proxy_backup_key_request: distributed user_backup_key to device", d.requesterDeviceId);
          reportE2eeError({ phase: "backup_key_distributed", deviceId: crypto.deviceId });
        } catch (err) {
          e2eeLog.warn("[Bug D] proxy_backup_key_request handler failed:", err);
        }
      }),
    );

    // ── DM Recovery (B): peer's recovered ciphertext arrived ─────────────
    // The sender re-encrypted a previously-failed DM and POSTed the new
    // per-device slot. Server pushes here. Update local ciphertext + decrypt
    // anew. Cancel any pending unrecoverable-timeout for this msgId.
    unsubs.push(
      ws.on("dm_recover_replaced", async (data) => {
        const d = data as unknown as {
          msgId?: string;
          channelId?: string;
          ciphertext?: string;
          senderId?: string;
          senderName?: string;
          contentType?: string;
          replyToId?: string | null;
          createdAt?: string;
        };
        if (!d.msgId || !d.channelId || !d.ciphertext) return;
        e2eeLog.log("← dm_recover_replaced", { msgId: d.msgId });

        const t = dmRecoveryTimers.get(d.msgId);
        if (t) {
          clearTimeout(t);
          dmRecoveryTimers.delete(d.msgId);
        }

        const channelMsgs = get().messages.get(d.channelId);
        let local = channelMsgs?.find((m) => m.id === d.msgId);
        if (!local) {
          // Fix 2: a partial send never delivered this message to us (no slot),
          // so we have no local record. Materialise it from the recovery payload
          // metadata, then decrypt the freshly-recovered ciphertext below.
          if (d.senderId && d.contentType && d.createdAt) {
            const created: Message = {
              id: d.msgId,
              channelId: d.channelId,
              senderId: d.senderId,
              senderName: d.senderName,
              ciphertext: d.ciphertext,
              contentType: d.contentType,
              replyToId: d.replyToId ?? null,
              createdAt: d.createdAt,
            };
            get().addMessage(created);
            local = created;
          } else {
            e2eeLog.warn("dm_recover_replaced for unknown local msg (no metadata)", d.msgId);
            return;
          }
        }

        // Invalidate caches that would short-circuit the re-decrypt: the
        // original failed attempt persisted `__NO_BACKUP__` to IDB and
        // recorded the msgId in `backupNotFound`. Without clearing those,
        // decryptSingleMessage(refreshed) returns "[Verschlüsselte Nachricht]"
        // before ever touching the freshly-recovered ciphertext.
        await deleteCachedPlaintext(d.msgId).catch(() => {});
        backupNotFound.delete(d.msgId);
        backupTransientSkipUntil.delete(d.msgId);
        ownMessageCache.delete(d.msgId);

        const refreshed: Message = { ...local, ciphertext: d.ciphertext, plaintext: undefined };
        const decrypted = await decryptSingleMessage(refreshed);
        if (decrypted) {
          get().updateMessage(d.msgId, d.channelId, {
            ciphertext: d.ciphertext,
            plaintext: decrypted.plaintext,
          });
        }
      }),
    );

    // ── DM Recovery (C): peer asks us to re-encrypt msgId for their device ─
    // Look up plaintext from local IDB cache and POST a fresh per-device slot.
    // If we don't have the plaintext (cache evicted, never sent from this
    // device), drop silently — the receiver's 10s unrecoverable-timeout will
    // surface a clear failure state.
    unsubs.push(
      ws.on("dm_recover_request", async (data) => {
        const d = data as unknown as {
          requesterUserId?: string;
          requesterDeviceId?: string;
          msgId?: string;
          ourDeviceId?: string;
          channelId?: string;
        };
        if (!d.msgId || !d.channelId || !d.requesterUserId || !d.requesterDeviceId) return;
        const crypto = useCrypto.getState();
        if (!crypto.initialized) return;

        e2eeLog.log("← dm_recover_request", { msgId: d.msgId, from: d.requesterUserId, device: d.requesterDeviceId });

        let plaintext = await getCachedPlaintext(d.msgId);
        if (!plaintext || !isRealPlaintext(plaintext)) {
          // F3 — recovery from ANOTHER active sender device. The original
          // sending device may be dead/offline (or its crypto state corrupt),
          // but the server fans the request out to every active device of the
          // sender. A device that never had this msg in its plaintext cache can
          // still fulfil recovery by recovering the plaintext from our own
          // E2EE per-user backup (any device with the user_backup_key), and as
          // a last resort the opt-in plaintext failsafe.
          const fromBackup = await tryBackupDecrypt({ id: d.msgId, channelId: d.channelId });
          if (typeof fromBackup === "string") {
            plaintext = fromBackup;
          } else {
            const fromFailsafe = await tryOwnPlaintextFallback(d.msgId);
            if (fromFailsafe) plaintext = fromFailsafe;
          }
        }
        if (!plaintext || !isRealPlaintext(plaintext)) {
          e2eeLog.warn("DM recovery: no plaintext available for", d.msgId, "— receiver will see unrecoverable");
          reportE2eeError({
            phase: "dm_decrypt_failed",
            senderId: d.requesterUserId,
            senderDeviceId: d.requesterDeviceId,
            deviceId: crypto.deviceId,
            error: `recovery_no_plaintext msgId=${d.msgId}`,
          });
          return;
        }

        // Tear down our (stale) session to the requester device so the next
        // encrypt builds a fresh X3DH session — exactly what the receiver
        // needs to be able to decrypt the recovered message.
        const { deleteSession } = await import("../lib/crypto-storage");
        await deleteSession(d.requesterUserId, d.requesterDeviceId).catch(() => {});

        // Re-encrypt for the requester's user. encryptForAllDevices fans out
        // to ALL of the requester's devices (and self), but we only need the
        // slot for d.requesterDeviceId — filter and POST that one.
        // Side note: this consumes one fresh one-time pre-key per device of
        // the requester. Acceptable for a recovery; not worth a single-device
        // encrypt path right now.
        try {
          const allRecipients = await crypto.encryptForAllDevices([d.requesterUserId], plaintext);
          const slot = allRecipients.find((r) => r.deviceId === d.requesterDeviceId);
          if (!slot) {
            e2eeLog.warn("DM recovery: encryptForAllDevices produced no slot for", d.requesterDeviceId);
            return;
          }
          await api.post(
            `/channels/${d.channelId}/messages/${d.msgId}/recover-recipient`,
            {
              recipientDeviceId: d.requesterDeviceId,
              ciphertext: slot.ciphertext,
              header: slot.header,
            },
            { raw: true },
          );
          e2eeLog.log("✅ DM recovery posted for", d.msgId, "→", d.requesterDeviceId);
        } catch (err) {
          e2eeLog.warn("DM recovery encrypt+POST failed:", err);
        }
      }),
    );

    // ── Typing expiry: clear stale typing indicators every 2s ─────────
    // If a typing_stop event is lost (WS reconnect, network blip, sender
    // crash), the indicator stays forever. This interval evicts entries
    // older than TYPING_EXPIRY_MS so ghost-typing auto-resolves.
    const TYPING_EXPIRY_MS = 5_000;
    const typingExpiryTimer = setInterval(() => {
      const now = Date.now();
      const current = get().typingUsers;
      let changed = false;
      const updated = new Map<string, TypingUser[]>();
      for (const [chId, users] of current) {
        const alive = users.filter((u) => now - u.timestamp < TYPING_EXPIRY_MS);
        if (alive.length !== users.length) changed = true;
        if (alive.length > 0) updated.set(chId, alive);
      }
      if (changed) set({ typingUsers: updated });
    }, 2_000);
    unsubs.push(() => clearInterval(typingExpiryTimer));

    return () => unsubs.forEach((fn) => fn());
  },
}));
